import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import RoundPlusButton from '../components/RoundPlusButton'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { createCheckin, updateCheckin, deleteCheckin, deleteCheckins, duplicateCheckin, retimeCheckin, retimeCheckins, suggestCheckinTimes, CheckinTimeAssignment, createCheckinTemplate, parseCheckinViaJob, listJobs, getCheckinsByIds, bulkParseCheckins, searchCollectionItems, getCollectionItem, extractQuotaError, extractDetail, discussCheckinsInGenie, getCheckinNudges, dismissCheckinNudge, scanCheckinRepetition, Feature, Checkin, CheckinNudge, CheckinTemplate, CheckinCreate, CheckinFeature, CheckinProject, Project, ManualOverrideType, CollectionItem, LlmQuotaError, UX_MOMENT_KEYS, setDeviceLocationPref } from '../api/client'
import { QuotaBanner } from '../components/QuotaBanner'
import { hasActiveCheckinFeatureRow, ownerOf } from '../utils/checkinFeatures'
import { useCheckinsData } from '../hooks/useCheckinsData'
import { useParseActivity, emptyParseActivity, ParseActivity, ParseTracker } from '../hooks/useParseActivity'
import { useNotify } from '../components/notice/useNotify'
import { ComposerCheckin } from '../components/CheckinComposer'
import CheckinItem from '../components/CheckinItem'
import CheckinTemplatePills from '../components/checkin/CheckinTemplatePills'
import CheckinNudgeCard, { NudgeAction, selectVisibleNudge } from '../components/checkin/CheckinNudge'
import ConfirmationModal from '../components/ConfirmationModal'
import LocationPrimerModal from '../components/checkin/LocationPrimerModal'
import { useDeviceLocation } from '../hooks/useDeviceLocation'
import { useOnceEver } from '../hooks/useOnceEver'
import debounce from 'lodash/debounce'
import { normalizeBody, hasSubstantialNewText, isAlreadyParsed } from '../utils/parseGating'
import Spinner from '../components/Spinner'
import { AnimatePresence, motion } from 'motion/react'
import { GripVertical } from 'lucide-react'
import {
    DndContext,
    DragEndEvent,
    UniqueIdentifier,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    SortableContext,
    SortingStrategy,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { REORDER_PRECISION, dropDay, dayBoundary, isDayBoundary, DayBoundary, TimelineSlot, TIMESTAMP_DISPLAY_MODES, dropNeedsTimeAssignment, retimeForReorder, sortDayByTime } from '../utils/dates'
import { TIMESTAMP_DISPLAY_LABELS, useTimestampDisplay } from '../hooks/useTimestampDisplay'
import CheckinTimeAssignmentDialog, { TimeAssignmentRow } from '../components/CheckinTimeAssignmentDialog'

// Constants for auto-save and AI parse timings
const AUTO_SAVE_DELAY_MS = 300 // Delay for auto-save after typing
// Auto-parse scheduling. A parse of a mid-size entry costs ~4 cents and takes
// 2-7s, so the question is not "has the user paused?" but "is there enough new
// text to be worth one?". Parsing on every 1.2s pause with no other gate had
// one 5,250-char entry parsed 189 times in 46 minutes, for $7.67.
//
// Replaying that day's real sessions, these values cut 618 parses to 130 (-79%
// of spend) while leaving short check-ins -- the common case, and always the
// first parse of any check-in -- as responsive as they are today.
const AI_PARSE_IDLE_MS = 2000       // pause before parsing when there's real new text
const AI_PARSE_SETTLED_MS = 15000   // pause before parsing regardless (backstop)
const AI_PARSE_MIN_NEW_CHARS = 200  // "real new text", ~2-3 sentences

const NEW_CHECKIN_ID_PLACEHOLDER = -1 // Placeholder ID for new checkins

// How long a retimed checkin shows its new time before letting the display
// mode have the slot back. Long enough to read a clock, short enough that it
// is plainly a confirmation rather than a new fixture on the row.
const TIME_FLASH_MS = 2200

/** The calendar day a checkin belongs to. `applies_to` is day-granular on the
 *  wire, but slicing rather than trusting that keeps a full ISO string from a
 *  future caller from silently making every row its own day. */
const dayOf = (checkin: { applies_to: string }): string => String(checkin.applies_to).slice(0, 10)

/**
 * Re-sort one day of the list by time — untimed at midnight, `created_at`
 * breaking ties — leaving every other row at the index it already had. The
 * day's rows must already be contiguous.
 */
const resortDay = (list: Checkin[], day: string, ascending: boolean): Checkin[] => {
    const positions: number[] = []
    list.forEach((c, i) => { if (dayOf(c) === day) positions.push(i) })
    const sorted = sortDayByTime(positions.map(i => list[i]), ascending)
    const out = [...list]
    positions.forEach((pos, k) => { out[pos] = sorted[k] })
    return out
}

/**
 * Order the whole timeline the way the server does — days in the sort's
 * direction, each day by `sortDayByTime`. For a row that changed day, possibly
 * to one with nothing else on screen, where patching one day's slice can't
 * place it.
 */
const sortTimelineByDay = (rows: Checkin[], ascending: boolean): Checkin[] => {
    const days = Array.from(new Set(rows.map(dayOf)))
        .sort((a, b) => (ascending ? a.localeCompare(b) : b.localeCompare(a)))
    return days.flatMap(d => sortDayByTime(rows.filter(c => dayOf(c) === d), ascending))
}

/**
 * Put a retimed checkin where the server will put it, without waiting for a
 * refetch. `dropped` is the list in the order the drop produced, so a row
 * moved from another day already sits inside its new day's block; it takes
 * the new day and time, and that day is re-sorted.
 *
 * Deliberately *not* "drop it where the pointer left it". A midpoint may
 * collide with a time a neighbour already has, and then `created_at` decides,
 * which can be the other side of where the user aimed. Showing the stored
 * order rather than the dropped one means the row does not jump on the next
 * fetch, and the timeline never claims an order the database would not give
 * back.
 */
const applyLocalRetime = (
    dropped: Checkin[],
    day: string,
    movedId: number,
    appliesAt: string,
    ascending: boolean,
): Checkin[] => resortDay(
    dropped.map(c => (c.id === movedId
        ? { ...c, applies_to: day, applies_at: appliesAt, applies_precision: REORDER_PRECISION }
        : c)),
    day,
    ascending,
)

// The indicator with nothing behind it: an indeterminate comb, "Analyzing...",
// and no chevron, because there is nothing to expand into. Stands in for the
// stretch of a parse before its socket has said anything. Module-level so it
// keeps one identity across renders.
const ANALYZING_ONLY: ParseActivity = emptyParseActivity(0)

/**
 * What to hand `CheckinItem` for one row, which is two questions rather than
 * one.
 *
 * While this check-in's own indicator is up, the panel belongs to the parse
 * running **now**: a record that has already reported its outcome describes a
 * different, finished parse, and the stepless stand-in renders exactly the
 * indicator that existed before any of this — which is also what the window
 * before the socket opens should look like.
 *
 * With the indicator down, two records stay: one the reader deliberately opened
 * out of the store ("Show analysis"), and one they had **open while it ran**.
 * That second case is the whole of this function's history: the panel used to
 * vanish the instant the work finished, which took the answer away at exactly
 * the moment it became readable, and from the one reader who had said they were
 * watching (reported 2026-09-22). It stays until the chevron closes it.
 * Everything else goes, or a finished parse's panel would linger on a row
 * nobody asked to see.
 *
 * Exported and pure so the rule can be read and tested on its own; the page
 * only supplies the two inputs.
 */
export const panelActivity = (
    activity: ParseActivity | undefined,
    indicatorUp: boolean,
): ParseActivity | undefined => {
    if (indicatorUp) {
        return activity && activity.status === 'running' && !activity.review
            ? activity
            : ANALYZING_ONLY
    }
    if (!activity) return undefined
    return activity.review || activity.expanded ? activity : undefined
}

// How long the "Analyzing..." indicator is guaranteed to be on screen, counted
// from the moment `runAIParse` puts it up. This is a floor, not a pad: a parse
// slower than this is already past it and ends the instant its chips land, so
// only the sub-second case is held back — long enough for the word to be read
// rather than seen as a flicker.
const MIN_VISIBLE_MS = 600

// Distinguishes Places (system or any future user collection that carries
// lat/lng) from generic user collection items so the active-filter chip and
// the autocomplete suggestion render with location styling (📍 + name) rather
// than the orange "collection item" pill.
const isLocationLikeItem = (item: { data?: any; metadata?: any } | null | undefined): boolean => {
    if (!item) return false
    if (item.data?.lat != null && item.data?.lng != null) return true
    if (item.metadata?.collection_name === 'Places') return true
    return false
}

// Detects items from the People system collection. Mirrors `isLocationLikeItem`
// — used so the filter chip / autocomplete row renders as a sky-tinted "person"
// pill with avatar+name instead of the orange generic "collection item" pill.
// Heuristic: collection name is "People" OR the row carries the canonical
// `first name` field (matches user-promoted system Peoples regardless of name).
const isPersonLikeItem = (item: { data?: any; metadata?: any } | null | undefined): boolean => {
    if (!item) return false
    if (item.metadata?.collection_name === 'People') return true
    if (item.data && typeof item.data['first name'] === 'string') return true
    return false
}

// Transform a server `Checkin` into the shape the composer round-trips
// (`ComposerCheckin`). Exported (rather than scoped inside the component
// closure it's called from) so a unit test can pin down which fields
// survive the transform — historically hand-listing fields here has been
// a source of silent data-loss regressions, e.g. when `latitude`/
// `longitude` and the map `key_*` columns were first added.
export const getComposerCheckinFromCheckin = (checkin: Checkin): ComposerCheckin => {
    const features = []

    if (checkin.checkin_features && Array.isArray(checkin.checkin_features)) {
        features.push(...checkin.checkin_features.map(feature => ({
            feature_id: feature.feature_id,
            decimal_value: feature.decimal_value,
            freeform_value: feature.freeform_value,
            reference_value: feature.reference_value,
            // Coords-only Locations rows live entirely in lat/lng. Without
            // these the composer silently drops every pin on edit-open,
            // and the next save wipes them from the DB.
            latitude: feature.latitude,
            longitude: feature.longitude,
            // Map fields
            key_freeform_value: feature.key_freeform_value,
            key_decimal_value: feature.key_decimal_value,
            key_reference_value: feature.key_reference_value,
            manual_override: feature.manual_override
        })))
    }

    return {
        body: checkin.body,
        // FastAPI serializes the `date` column as YYYY-MM-DD, so pass it
        // through. The previous `new Date(...).toISOString()` round-trip
        // threw RangeError on the empty string the desktop date input
        // emits mid-keyboard-edit, crashing the whole timeline on render.
        applies_to: checkin.applies_to,
        // The time of day, when the checkin has one. Every save payload is
        // built from this transform, and the server reads a missing
        // `applies_at` as *no time* rather than *leave it alone* — so dropping
        // it here would silently untime a checkin on the next body keystroke.
        applies_at: checkin.applies_at ?? null,
        // And how that time was set, for the sharper half of the same reason:
        // an omitted precision on a checkin that *has* a time reads as
        // `exact`, so dropping it would not untime the row — it would quietly
        // promote a dragged or bucket-picked time to one the user typed.
        applies_precision: checkin.applies_precision,
        features,
        projects: checkin.checkin_projects,
        parse_generation: checkin.parse_generation
    }
}

// Fold Stage A's detected project ids into a checkin, optimistically, while
// the rest of the parse is still running.
//
// Two fields have to move together. `checkin_projects` is what the override
// logic and the save path read; `project_details` is what the chip renderer
// reads (`CheckinItem.tsx:431,440`). Writing only the first is why Stage A's
// detection used to be invisible until the whole parse came back — the ids
// were there, the chips had nothing to render from.
//
// Manual overrides carry over so this optimistic view matches what
// `_reconcile_checkin_projects` will persist: a DELETE tombstone (or a
// KEEP_VALUE pin) the user just set must survive re-detection, otherwise a
// project they removed but still mentioned in the body flashes back as an
// active tag.
//
// Exported for the same reason `getComposerCheckinFromCheckin` is: it is the
// interesting half of a callback buried in the component closure, and it is
// pure.
export const applyStageAProjects = (
    checkin: Checkin,
    detectedProjectIds: number[],
    knownProjects: Project[],
): Checkin => {
    const existing = checkin.checkin_projects || []
    // Owned rows — the user's or Genie's — carry over whole, actor included.
    const ownedById = new Map(
        existing
            .filter(p => ownerOf(p) !== 'parser')
            .map(p => [p.project_id, p])
    )
    const detectedSet = new Set(detectedProjectIds)
    const detected: CheckinProject[] = detectedProjectIds.map(id =>
        ownedById.get(id) ?? { project_id: id, manual_override: null }
    )
    // Keep owned projects the detector didn't surface this run (e.g. a
    // tombstoned project no longer mentioned).
    const preserved = existing.filter(
        p => ownerOf(p) !== 'parser' && !detectedSet.has(p.project_id)
    )
    const checkinProjects = [...detected, ...preserved]

    // Resolve the chips against the projects already loaded on the page.
    // A project the list hasn't caught up with (created by this very parse,
    // say) falls back to whatever detail the checkin was already carrying, so
    // this write can only add chips, never drop one.
    const byId = new Map(knownProjects.map(p => [p.id, p]))
    const carried = new Map((checkin.project_details ?? []).map(p => [p.id, p]))
    const projectDetails = checkinProjects
        .map(cp => byId.get(cp.project_id) ?? carried.get(cp.project_id))
        .filter((p): p is Project => p != null)

    return { ...checkin, checkin_projects: checkinProjects, project_details: projectDetails }
}

const personDisplayName = (item: { data?: any; metadata?: any } | null | undefined): string => {
    const data = item?.data || {}
    const first = typeof data['first name'] === 'string' ? data['first name'].trim() : ''
    const last = typeof data['last name'] === 'string' ? data['last name'].trim() : ''
    if (first && last) return `${first} ${last}`
    if (first) return first
    if (last) return last
    const nickname = typeof data.nickname === 'string' ? data.nickname.trim() : ''
    if (nickname) return nickname
    return item?.metadata?.display_text || 'Person'
}

const personInitials = (item: { data?: any } | null | undefined): string => {
    const data = item?.data || {}
    const first = typeof data['first name'] === 'string' ? data['first name'].trim() : ''
    const last = typeof data['last name'] === 'string' ? data['last name'].trim() : ''
    const fc = first ? first[0]!.toUpperCase() : ''
    const lc = last ? last[0]!.toUpperCase() : ''
    const combined = `${fc}${lc}`
    if (combined) return combined
    const nickname = typeof data.nickname === 'string' ? data.nickname.trim() : ''
    if (nickname) return nickname[0]!.toUpperCase()
    return '?'
}

/**
 * Wrapper around one timeline row, so a checkin that just landed at the top —
 * from a template tap or from Duplicate — springs in rather than appearing.
 *
 * It wraps *every* row, not only the landing one, because a conditional
 * wrapper would remount the CheckinItem underneath it the moment the landing
 * flag cleared. `initial={false}` is motion's own way of saying "mount at the
 * animate values"; since `initial` is read once, at mount, the page can leave
 * `landedCheckinId` set afterwards without the entrance replaying.
 *
 * `nudge` rides inside the same wrapper so the card travels with its checkin —
 * it belongs to that row, not to the gap below it, and a nudge on a landing row
 * should spring in as part of the row rather than separately.
 */
const CheckinRow: React.FC<{
    landed: boolean
    nudge?: React.ReactNode
    children: React.ReactNode
}> = ({ landed, nudge, children }) => (
    <motion.div
        initial={landed ? { opacity: 0, y: -16, scale: 0.97 } : false}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
    >
        {children}
        {nudge}
    </motion.div>
)

/**
 * One timeline row, armed for drag-to-reorder.
 *
 * Renders through a child function so the grip can be planted *inside* the
 * card, in its header's control cluster — dnd-kit's listeners have to ride the
 * element the user actually grabs, and a check-in card is full of interactive
 * parts (chips that filter, a map, an actions menu, the whole body as an edit
 * target) that arming the card wholesale would fight. Same shape as the
 * dashboard's summary cards, for the same reason.
 *
 * `disabled` covers the rows a reorder is meaningless on: the unsaved
 * placeholder, and every row while the list is sorted by relevance rather than
 * by time.
 */
const SortableCheckin: React.FC<{
    id: number
    disabled: boolean
    children: (handle: React.ReactNode) => React.ReactNode
}> = ({ id, disabled, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id, disabled })

    const handle = disabled ? null : (
        <button
            type="button"
            aria-label="Reorder checkin"
            title="Drag to move this checkin"
            {...attributes}
            {...listeners}
            // `touch-none` so a drag begun on the grip isn't taken over by the
            // page's own scroll; `select-none` plus the webkit callout reset so
            // a press-and-hold arms the drag instead of starting iOS/Android
            // text selection on the checkin body underneath.
            style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
            className="cursor-grab touch-none select-none p-1 text-gray-300 transition-colors hover:text-gray-500 active:cursor-grabbing"
        >
            <GripVertical className="h-5 w-5" />
        </button>
    )

    return (
        <div
            ref={setNodeRef}
            style={{
                // Translate only: dnd-kit scales the dragged row to the size of
                // the row it's over, which squashed it mid-drag.
                transform: CSS.Translate.toString(transform),
                transition,
                // Lift the travelling row over the ones shuffling under it,
                // on a card of its own so they don't show through. The white
                // ring pads the card without changing the row's size mid-drag.
                zIndex: isDragging ? 10 : undefined,
                position: isDragging ? 'relative' : undefined,
                boxShadow: isDragging
                    ? '0 0 0 12px #fff, 0 12px 28px -4px rgb(0 0 0 / 0.25)'
                    : undefined,
            }}
            className={isDragging ? 'rounded-md bg-white' : undefined}
            data-testid="checkin-slot"
            data-checkin-id={id}
        >
            {children(handle)}
        </div>
    )
}

/**
 * The timeline's rows in year groups, years in the sort's direction. The page
 * renders these groups, so their concatenation is the display order the
 * reorder has to agree with.
 */
const groupByYear = (rows: Checkin[], ascending: boolean): { year: number; rows: Checkin[] }[] => {
    const byYear = new Map<number, Checkin[]>()
    rows.forEach(c => {
        const year = new Date(c.applies_to).getFullYear()
        const group = byYear.get(year)
        if (group) group.push(c)
        else byYear.set(year, [c])
    })
    return Array.from(byYear.keys())
        .sort((a, b) => (ascending ? a - b : b - a))
        .map(year => ({ year, rows: byYear.get(year)! }))
}

/** A day divider as the page holds it: the rule's `DayBoundary`, plus a
 *  sortable id that can't collide with a numeric checkin id, and the day
 *  shown below it, which its label names. */
type BoundarySlot = DayBoundary & { key: string; below: string }
type DisplaySlot = TimelineSlot<Checkin> | BoundarySlot

/**
 * The reorderable timeline as displayed: every row, with a divider between
 * each two consecutive rows whose days differ, year changes included. None
 * before the first row or after the last.
 */
const timelineSlots = (rows: Checkin[], ascending: boolean): DisplaySlot[] => {
    const displayed = groupByYear(rows, ascending).flatMap(g => g.rows)
    const out: DisplaySlot[] = []
    const keys = new Set<string>()
    displayed.forEach((row, i) => {
        const prev = displayed[i - 1]
        if (prev && dayOf(prev) !== dayOf(row)) {
            let key = `boundary:${dayOf(prev)}:${dayOf(row)}`
            // Only a list whose days aren't contiguous could repeat a pair;
            // the ids must stay unique regardless.
            for (let n = 2; keys.has(key); n++) key = `boundary:${dayOf(prev)}:${dayOf(row)}:${n}`
            keys.add(key)
            out.push({ ...dayBoundary(dayOf(prev), dayOf(row)), key, below: dayOf(row) })
        }
        out.push(row)
    })
    return out
}

const slotId = (slot: DisplaySlot): UniqueIdentifier =>
    isDayBoundary(slot) ? (slot as BoundarySlot).key : slot.id

/**
 * `verticalListSortingStrategy`, taught that dividers take no room.
 *
 * The stock strategy shifts each item by the dragged row's height plus the gap
 * it measures to that item's neighbour. A divider is a zero-height slot sitting
 * at the top of the row below it, so that measured gap is 24px on one side of
 * it and 0 on the other, and rows beyond a divider would shuffle 24px out of
 * line. Here every displaced slot moves by one row pitch — the dragged row's
 * height plus the gap between rows — and the dragged row itself, where the
 * strategy places it, travels past the rows in between and not the dividers.
 */
const reorderStrategy = (slots: DisplaySlot[]): SortingStrategy =>
    ({ activeIndex, activeNodeRect, index, rects, overIndex }) => {
        const active = rects[activeIndex] ?? activeNodeRect
        if (!active || overIndex < 0) return null
        const rowAt = (k: number) => k >= 0 && k < slots.length && !isDayBoundary(slots[k]) && rects[k]
        const gaps: number[] = []
        for (let k = activeIndex + 1; k < slots.length; k++) {
            if (rowAt(k)) { gaps.push(rects[k].top - (active.top + active.height)); break }
        }
        for (let k = activeIndex - 1; k >= 0; k--) {
            if (rowAt(k)) { gaps.push(active.top - (rects[k].top + rects[k].height)); break }
        }
        const gap = gaps.length ? Math.max(0, Math.min(...gaps)) : 0
        const shift = (y: number) => ({ x: 0, y, scaleX: 1, scaleY: 1 })

        if (index === activeIndex) {
            const [lo, hi] = overIndex > activeIndex ? [activeIndex + 1, overIndex] : [overIndex, activeIndex - 1]
            let travel = 0
            for (let k = lo; k <= hi; k++) if (rowAt(k)) travel += rects[k].height + gap
            return shift(overIndex > activeIndex ? travel : -travel)
        }
        const pitch = active.height + gap
        if (index > activeIndex && index <= overIndex) return shift(-pitch)
        if (index < activeIndex && index >= overIndex) return shift(pitch)
        return shift(0)
    }

/** Where a divider's line sits above the row below it: the middle of the
 *  `space-y-6` gap between two rows. */
const DIVIDER_LINE_OFFSET_PX = -12

/**
 * The line between two days, shown only while a row is being dragged.
 *
 * It is a slot of its own in the sortable list, so the gap between two days
 * is two drop positions, above the line and below it. It can't be picked up,
 * but it is a drop target: `disabled` covers only the draggable half.
 *
 * It takes no room. The element is empty and zero-height with no margin of
 * its own, so the spacing container's margins collapse straight through it and
 * the rows sit exactly where they would without it. The line is absolutely
 * positioned up into the gap, and fades rather than mounting, so the list of
 * sortable ids is the same whether or not a drag is on.
 */
const DayDivider: React.FC<{ id: string; label: string; visible: boolean }> = ({ id, label, visible }) => {
    const { setNodeRef, transform, transition } = useSortable({
        id,
        disabled: { draggable: true, droppable: false },
    })
    return (
        <div
            ref={setNodeRef}
            aria-hidden
            data-testid="day-divider"
            data-divider-id={id}
            style={{
                position: 'relative',
                // Above the dragged row's card (`zIndex: 10` in
                // SortableCheckin). Its white ring reaches into the gap where
                // the line sits, so the line nearest the dragged row -- the
                // one being aimed at -- would otherwise be the one hidden.
                zIndex: 20,
                height: 0,
                margin: 0,
                transform: CSS.Translate.toString(transform),
                transition,
            }}
        >
            <AnimatePresence>
                {visible && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        className="pointer-events-none absolute inset-x-0 flex -translate-y-1/2 items-center gap-2"
                        style={{ top: DIVIDER_LINE_OFFSET_PX }}
                    >
                        <span className="text-xs text-gray-400">{label}</span>
                        <span className="h-px flex-1 bg-gray-200" />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

const CheckinsPage: React.FC = () => {
    const notify = useNotify()
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()
    const location = useLocation()

    // Get filter values from URL parameters
    const selectedCheckinId = searchParams.get('checkin_id') ? Number(searchParams.get('checkin_id')) : null
    const isSingleCheckinView = selectedCheckinId !== null
    const autoEditOnLoad = isSingleCheckinView && searchParams.get('edit') === '1'
    const selectedFeatureId = searchParams.get('feature') ? Number(searchParams.get('feature')) : null
    const selectedProjectId = searchParams.get('project') ? Number(searchParams.get('project')) : null
    const selectedCollectionItemId = searchParams.get('collection_item') ? Number(searchParams.get('collection_item')) : null
    const urlTextSearch = searchParams.get('text_search') || ''
    const sortOrder = searchParams.get('order') || (urlTextSearch ? 'most_relevant' : 'most_recent')
    const startDate = searchParams.get('start_date')
    const endDate = searchParams.get('end_date')
    const dateRemainder = searchParams.get('date_remainder')

    
    const selectedDateRange = useMemo(() => {
        if (startDate && endDate) {
            return {
                start_date: startDate,
                end_date: endDate,
                remainder: dateRemainder || ''
            }
        }
        return null
    }, [startDate, endDate, dateRemainder])
    const [filterQuery, setFilterQuery] = useState('')
    const [filteredItems, setFilteredItems] = useState<Array<{type: 'feature' | 'project' | 'collection_item', item: Feature | Project | CollectionItem}>>([])
    const [showFilterDropdown, setShowFilterDropdown] = useState(false)
    const [isFilterSearching, setIsFilterSearching] = useState(false)
    const [selectedItemIndex, setSelectedItemIndex] = useState(-1)
    const [collectionItemDisplayData, setCollectionItemDisplayData] = useState<CollectionItem | null>(null)
    const filterDropdownRef = useRef<HTMLDivElement>(null)
    const [showSortDropdown, setShowSortDropdown] = useState(false)
    const sortDropdownRef = useRef<HTMLDivElement>(null)
    // The sort menu carries the timestamp display mode too -- it already
    // governs how the list is presented, and the setting had nowhere on this
    // page to be found. The same hook backs the date-label tap in
    // `CheckinItem` and the account page's radios.
    const { mode: timestampMode, choose: chooseTimestampDisplay } = useTimestampDisplay()
    const [textSearchQuery, setTextSearchQuery] = useState(urlTextSearch)
    // The search bar is a disclosure behind the header's magnifier, not a
    // permanent fixture. It starts open only when the URL already carries a
    // text search, so a shared or bookmarked link shows its query in the field
    // rather than filtering the list from a collapsed box. Every *applied*
    // filter — the text search included — has its own removable chip below,
    // which is what makes collapsing safe: nothing filters invisibly.
    const [showSearch, setShowSearch] = useState(Boolean(urlTextSearch))
    const searchInputRef = useRef<HTMLInputElement>(null)
    // Clipping is only wanted while the row's height is actually moving. Left
    // on at rest it eats everything that paints outside the row's box — the
    // input's focus ring, and the whole suggestions panel, which is absolutely
    // positioned below the field (measured: 71 of its 83px). Starts false so a
    // bar that opens un-animated — the ?text_search= case, where
    // AnimatePresence's initial={false} means no animation runs and
    // onAnimationComplete never fires — is never left clipped.
    const [clipSearchRow, setClipSearchRow] = useState(false)

    // Use extracted data management hook
    const {
        checkins,
        features,
        recentFeatures,
        projects,
        collectionItems,
        loading,
        searchLoading,
        hasUpdatesAvailable,
        setHasUpdatesAvailable,
        fetchData,
        setCheckins,
        mergeCollectionItems,
        mergeFeatures,
        checkForUpdates,
        checkinsRef
    } = useCheckinsData({
        featureId: selectedFeatureId,
        projectId: selectedProjectId,
        collectionItemId: selectedCollectionItemId,
        textSearch: textSearchQuery,
        sortOrder,
        startDate: selectedDateRange?.start_date,
        endDate: selectedDateRange?.end_date,
        limit: 100,
        checkinId: selectedCheckinId
    })

    // `runAIParse` is memoized with empty deps, so everything it reaches
    // through is frozen at first render -- when this list is still empty,
    // because the fetch hasn't landed. Stage A resolves its project ids
    // against a ref for that reason.
    const projectsRef = useRef(projects)
    projectsRef.current = projects

    // UI-specific state that wasn't extracted
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; checkinId: number | null; checkinIds?: number[]; isDeleting: boolean }>({
        isOpen: false,
        checkinId: null,
        isDeleting: false
    })
    // The checkin most recently dropped in at the top of the list (template
    // tap, or Duplicate). Read once, when that row mounts, to give it an
    // entrance; harmless to leave set afterwards.
    const [landedCheckinId, setLandedCheckinId] = useState<number | null>(null)
    // The time a just-dragged checkin should flash, by id. Keyed rather than a
    // single id, so two drags in quick succession don't cancel each other's
    // feedback. Cleared by a timer per entry.
    const [timeFlashes, setTimeFlashes] = useState<Record<number, string>>({})
    const timeFlashTimers = useRef(new Map<number, number>())
    // The day a drop asked for times on, or null when no dialog is open. Set
    // only when the drop landed between two untimed neighbours, which is the
    // one case the midpoint rule has nothing to work with — see
    // `dropNeedsTimeAssignment`. `movesDay` when the dragged row comes from
    // another day, which confirming moves it out of.
    const [timeAssignment, setTimeAssignment] = useState<{
        day: string
        rows: TimeAssignmentRow[]
        placingId: number
        movesDay: boolean
    } | null>(null)
    // Whether a reorder drag is under way, which is what shows the day
    // dividers. They are mounted all along; only their lines come and go.
    const [reorderDragging, setReorderDragging] = useState(false)
    // Handed straight to the pill row so a just-saved template appears without
    // a refetch. The pill row owns the list; this is the one way in.
    const [savedTemplate, setSavedTemplate] = useState<CheckinTemplate | null>(null)
    // Active nudges, already filtered server-side (dismissed ones never come
    // back, and neither does one whose signature the user has already turned
    // into a template). Only one is ever on screen — `selectVisibleNudge`
    // picks it — but the whole list is held so a dismissal reveals the next
    // without a round trip.
    const [nudges, setNudges] = useState<CheckinNudge[]>([])
    const [nudgeBusy, setNudgeBusy] = useState(false)
    const [isSelectionMode, setIsSelectionMode] = useState(false)
    const [selectedCheckinIds, setSelectedCheckinIds] = useState<Set<number>>(new Set())
    const [dateRangeResult, setDateRangeResult] = useState<{
        date_found: boolean
        start_date?: string
        end_date?: string
        remainder: string
    } | null>(null)
    const [editingCheckinId, setEditingCheckinId] = useState<number | null>(null)
    const [showNewCheckinComposer, setShowNewCheckinComposer] = useState(false)
    const [savingState, setSavingState] = useState<'unsaved' | 'saving' | 'ai-parsing' | null>(null)
    const [isBulkParsing, setIsBulkParsing] = useState(false)
    const [analyzingCheckinIds, setAnalyzingCheckinIds] = useState<Set<number>>(new Set())
    // What each in-flight parse is actually doing, folded from the events its
    // socket sends. `analyzingCheckinIds` stays the one answer to "is the
    // indicator up" — it is seq-guarded, and the re-analyze path drives it too
    // — and this only supplies the detail behind it.
    const {
        activities: parseActivities,
        beginParse,
        watchJob,
        reviewParse,
        clearReview,
        setExpanded: setParseExpanded,
        setTechnical: setParseTechnical,
    } = useParseActivity()
    // `runAIParse` is memoized with empty deps; `beginParse` is stable, but the
    // ref keeps that from being something a reader has to verify.
    const beginParseRef = useRef(beginParse)
    beginParseRef.current = beginParse
    // See `panelActivity`.
    const activityFor = useCallback(
        (checkinId: number): ParseActivity | undefined =>
            panelActivity(parseActivities[checkinId], analyzingCheckinIds.has(checkinId)),
        [parseActivities, analyzingCheckinIds],
    )
    // The chevron on a reviewed record closes the review rather than collapsing
    // it to a line about a parse that is over; on a live one it is the ordinary
    // disclosure it has always been.
    const handleToggleParseExpanded = useCallback(
        (checkinId: number, expanded: boolean) => {
            if (!expanded && parseActivities[checkinId]?.review) {
                clearReview(checkinId)
                return
            }
            setParseExpanded(checkinId, expanded)
        },
        [parseActivities, clearReview, setParseExpanded],
    )
    // Which record's "Details" tier moved. Per check-in, because two analyses
    // can be open at once and reading the call tree of one must not switch the
    // other (reported 2026-09-23); the sticky preference still decides what
    // the next one opens at.
    const handleToggleParseTechnical = useCallback(
        (checkinId: number, technical: boolean) => setParseTechnical(checkinId, technical),
        [setParseTechnical],
    )
    // The parse timer currently armed, so the composer can show how long is
    // left and offer to skip the wait. `armedAt`/`dueAt` rather than a
    // remaining-time number: the ring animates between the two, so nothing
    // here has to tick.
    const [parseCountdown, setParseCountdown] = useState<
        { checkinId: number; armedAt: number; dueAt: number } | null
    >(null)
    // Which of the two debounces the countdown belongs to. lodash's debounce
    // has no `pending()` in 4.18, so flushing the right one means tracking it.
    const armedParseTimerRef = useRef<'idle' | 'settled' | null>(null)
    const [quotaError, setQuotaError] = useState<LlmQuotaError | null>(null)
    const [showActionMenu, setShowActionMenu] = useState(false)
    const actionMenuRef = useRef<HTMLDivElement>(null)
    const [showBulkActionMenu, setShowBulkActionMenu] = useState(false)
    const bulkActionMenuRef = useRef<HTMLDivElement>(null)
    const [showReanalyzeConfirm, setShowReanalyzeConfirm] = useState(false)
    const savingStateRef = useRef<'unsaved' | 'saving' | 'ai-parsing' | null>(null)
    // Real in-flight tracker keyed by checkin id. Distinct from savingState
    // (which is the UI indicator and lags React commits); used by queueAIParse
    // to actually serialize parse-after-save and avoid the FK race where the
    // PUT's INSERTed reference_value points at a collection_item that
    // the parse just GC'd as orphan.
    const pendingSavesRef = useRef<Map<number, Promise<void>>>(new Map())
    // In-flight parse tracker keyed by checkin id. The debounced auto-parse can
    // fire again while a previous parse for the same checkin is still awaiting
    // the LLM; overlapping parses each materialize CREATE_NEW collection items
    // before either commits, producing duplicates. queueAIParse waits on this
    // so parses of one checkin run serially. (The backend also dedups + locks;
    // this just avoids piling up redundant LLM parses.)
    const pendingParsesRef = useRef<Map<number, Promise<void>>>(new Map())
    // Normalized body of the last parse we actually sent, keyed by checkin id.
    // The whitespace guard in handleFieldEdit compares against the checkins
    // list, which has already advanced to the keystroke being handled, so it
    // only catches whitespace-only edits between consecutive keystrokes. This
    // compares against what the parser last saw, which is the question that
    // decides whether a parse can tell us anything new.
    const lastParsedBodyRef = useRef<Map<number, string>>(new Map())
    // Monotonic request counter per checkin. The debounce collapses calls
    // within its window, but a parse takes several seconds and the awaits
    // below are not the debounce -- a second invocation can start, queue
    // behind the first, and resume holding a body the user has since typed
    // past. Each invocation claims a sequence number up front and drops
    // itself after the awaits if a newer one has claimed a higher number.
    const parseRequestSeqRef = useRef<Map<number, number>>(new Map())

    const mountedRef = useRef(true)
    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    // Watch one check-in's parse job to its end: the indicator up and its
    // record folding meanwhile, the row refreshed once it is done. The parse
    // is the server's; this only watches it. Never rejects — it says how the
    // job ended, and leaves surfacing a quota error to the caller.
    const followParseJob = useCallback(
        async (
            checkinId: number,
            jobId: string,
        ): Promise<{ ok: boolean; quota: LlmQuotaError | null }> => {
            setAnalyzingCheckinIds(prev => new Set(prev).add(checkinId))
            const watched = watchJob(checkinId, jobId).then(() => undefined)
            // A composer parse of this check-in waits for this one, as it
            // waits for any parse in flight.
            pendingParsesRef.current.set(checkinId, watched)
            try {
                await watched
                const fresh = await getCheckinsByIds([checkinId])
                const checkin = fresh.checkins.find(c => c.id === checkinId)
                if (!checkin) return { ok: false, quota: null }
                if (mountedRef.current) {
                    mergeCollectionItems(fresh.collection_items)
                    setCheckins(prev => prev.map(c => (c.id === checkinId ? checkin : c)))
                }
                return { ok: true, quota: null }
            } catch (err) {
                const quota = extractQuotaError(err)
                if (!quota) console.error(`Parse job ${jobId} for checkin ${checkinId} failed:`, err)
                return { ok: false, quota }
            } finally {
                if (pendingParsesRef.current.get(checkinId) === watched) {
                    pendingParsesRef.current.delete(checkinId)
                }
                if (mountedRef.current) {
                    setAnalyzingCheckinIds(prev => {
                        const next = new Set(prev)
                        next.delete(checkinId)
                        return next
                    })
                }
            }
        },
        [watchJob, mergeCollectionItems, setCheckins],
    )

    // A parse run as a job outlives the page that asked for it, so a reload
    // mid-parse finds it still running: put its indicator back up and keep
    // folding its record.
    useEffect(() => {
        let gone = false
        void (async () => {
            try {
                const live = await listJobs({ status: ['queued', 'running'], subjectType: 'checkin' })
                if (gone) return
                for (const job of live) {
                    if (job.kind === 'checkin_parse' && job.subject_id !== null) {
                        void followParseJob(job.subject_id, job.job_id).then(({ quota }) => {
                            if (quota && !gone) setQuotaError(quota)
                        })
                    }
                }
            } catch (err) {
                console.error('Failed to look for parses still running:', err)
            }
        })()
        return () => {
            gone = true
        }
        // Once per mount: the jobs worth resuming are the ones running when
        // the page loaded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    const showNewCheckinComposerRef = useRef<boolean>(showNewCheckinComposer)
    const allowSaveNewCheckinRef = useRef(false)
    // Stable React keys per checkin slot. Defaults to String(id), but the
    // composer placeholder gets an opaque string key when created and that
    // same key is carried over to the real id after save — so the slot's
    // CheckinItem instance survives the id flip (`-1` → real) without
    // unmounting, preserving textarea focus and avoiding remount fades.
    // Also lets `key={id}` for other rows so a prepend doesn't shift every
    // CheckinItem by an index and trigger AnimatePresence churn on chips.
    const slotKeyRef = useRef<Map<number, string>>(new Map())
    const keyFor = (checkin: Checkin): string =>
        slotKeyRef.current.get(checkin.id) ?? String(checkin.id)

    // Debug logging for hasUpdatesAvailable changes
    useEffect(() => {
        console.log('[Update Notification] hasUpdatesAvailable changed to:', hasUpdatesAvailable)
    }, [hasUpdatesAvailable])

    // Update saving state ref when it changes
    useEffect(() => {
        savingStateRef.current = savingState
    }, [savingState])
     
    // Allow one new object per every time we use the add button
    useEffect(() => {
        if (showNewCheckinComposer && !allowSaveNewCheckinRef.current) {
            allowSaveNewCheckinRef.current = true
        }
        showNewCheckinComposerRef.current = showNewCheckinComposer
    }, [showNewCheckinComposer])

     
    // Track edit timestamps for each checkin to handle conflicts between saves and AI parsing
    const editTimestampsRef = useRef<Map<number, {
        body: number,
        features: Map<number, number>,
        projects: Map<number, number>
    }>>(new Map())

    const firstElementIsEmptyCheckin = (checkins: Checkin[]) => {
        if (checkins.length === 0) return false
        const firstCheckin = checkins[0]
        if ((firstCheckin.id !== NEW_CHECKIN_ID_PLACEHOLDER) ||
            (firstCheckin.body !== "") ||
            (firstCheckin.checkin_features.length !== 0)) {
            return false
        }
        return true
    }

    const handleDiscussCheckins = async (ids: number[]) => {
        if (ids.length === 0) return
        try {
            const chat = await discussCheckinsInGenie(ids)
            navigate(`/genie/${chat.id}`)
        } catch (err) {
            console.error('Failed to open discussion in Genie:', err)
            notify.error('Failed to open discussion in Genie')
        }
    }

    useEffect(() => {
        fetchData(true)
    }, [])

    // Arriving from a template tap on the Dashboard: the apply already happened
    // there, and the navigation carries the new checkin's id so its row gets the
    // same entrance it would have got from a tap on this page. Setting the flag
    // here is early enough — the list is still loading, so the row has not
    // mounted yet, and CheckinRow reads `initial` once at mount.
    //
    // The state is then dropped from the history entry: it belongs to the
    // navigation, not to the URL, so coming back here later must not replay the
    // entrance. Same path and search, so the replace is invisible.
    useEffect(() => {
        const landed = (location.state as { landedCheckinId?: number } | null)?.landedCheckinId
        if (typeof landed !== 'number') return
        setLandedCheckinId(landed)
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
    }, [location.state, location.pathname, location.search, navigate])

    // Load collection item data when collection item ID changes
    useEffect(() => {
        if (selectedCollectionItemId) {
            getCollectionItem(selectedCollectionItemId)
                .then(item => {
                    setCollectionItemDisplayData(item)
                })
                .catch(err => {
                    console.error('Failed to fetch collection item:', err)
                    setCollectionItemDisplayData(null)
                })
        } else {
            setCollectionItemDisplayData(null)
        }
    }, [selectedCollectionItemId])

    // Refetch data when filters change
    useEffect(() => {
        if (!loading) { // Don't refetch during initial load
            fetchData()
        }
    }, [selectedFeatureId, selectedProjectId, selectedCollectionItemId, sortOrder, selectedDateRange, selectedCheckinId])

    // Honor ?edit=1 on the single-checkin view: open the editor once data lands, then strip the flag.
    useEffect(() => {
        if (!autoEditOnLoad) return
        const target = checkins.find(c => c.id === selectedCheckinId)
        if (!target) return
        setEditingCheckinId(target.id)
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('edit')
        setSearchParams(newSearchParams, { replace: true })
    }, [autoEditOnLoad, checkins, selectedCheckinId])

    // Update text search when URL parameters change
    useEffect(() => {
        const urlTextSearchParam = searchParams.get('text_search') || ''
        if (urlTextSearchParam !== textSearchQuery) {
            setTextSearchQuery(urlTextSearchParam)
        }
    }, [searchParams])

    // Refetch data when text search query changes
    useEffect(() => {
        if (!loading) { // Don't refetch during initial load
            fetchData()
        }
    }, [textSearchQuery])

    // Debounced filter search function
    const debouncedFilterSearch = useCallback(
        debounce(async (query: string) => {
            if (query.trim()) {
                setIsFilterSearching(true)
                try {
                    // Search for features, projects, and collection items in parallel
                    const [filteredFeatures, filteredProjects, collectionItems] = await Promise.all([
                        // Filter features based on query
                        Promise.resolve(features.filter(f =>
                            f.title.toLowerCase().includes(query.toLowerCase())
                        ).map(f => ({ type: 'feature' as const, item: f }))),

                        // Filter projects based on query
                        Promise.resolve(projects.filter(p =>
                            p.title.toLowerCase().includes(query.toLowerCase()) ||
                            (p.emoji && p.emoji.includes(query))
                        ).map(p => ({ type: 'project' as const, item: p }))),

                        // Search collection items via API
                        searchCollectionItems(query, 5).then(items =>
                            items.map(item => ({ type: 'collection_item' as const, item }))
                        ).catch(err => {
                            console.error('Error searching collection items:', err)
                            return []
                        })
                    ])

                    setFilteredItems([...filteredFeatures, ...filteredProjects, ...collectionItems])
                    setSelectedItemIndex(-1)
                    
                    // Parse date from query text
                    try {
                        const response = await fetch(`/api/features/parse-date?${new URLSearchParams({ query }).toString()}`)
                        if (response.ok) {
                            const result = await response.json()
                            setDateRangeResult(result)
                        } else {
                            setDateRangeResult(null)
                        }
                    } catch (err) {
                        console.error('Error parsing date:', err)
                        setDateRangeResult(null)
                    }
                } catch (err) {
                    console.error('Error filtering items:', err)
                } finally {
                    setIsFilterSearching(false)
                }
                setShowFilterDropdown(true)
            } else {
                setFilteredItems([])
                setShowFilterDropdown(false)
                setSelectedItemIndex(-1)
                setDateRangeResult(null)
            }
        }, 300),
        [features, projects]
    )


    // Handle filter search input
    const handleFilterSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value
        setFilterQuery(query)
        setSelectedItemIndex(-1)
        debouncedFilterSearch(query)
    }

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Escape is the one key that means something whether or not the
        // dropdown is up, so it has to be handled above the guard below:
        // with the dropdown open it dismisses that, and with nothing typed it
        // collapses the bar. An *applied* search survives either way — its
        // chip below is the way to drop that.
        if (e.key === 'Escape') {
            setShowFilterDropdown(false)
            setSelectedItemIndex(-1)
            if (!filterQuery) setShowSearch(false)
            return
        }

        if (!showFilterDropdown) return

        const totalItems = filteredItems.length + 1 // +1 for text search option

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setSelectedItemIndex(prev => prev < totalItems - 1 ? prev + 1 : 0)
                break
            case 'ArrowUp':
                e.preventDefault()
                setSelectedItemIndex(prev => prev > 0 ? prev - 1 : totalItems - 1)
                break
            case 'Enter':
                e.preventDefault()
                if (filterQuery && selectedItemIndex === 0) {
                    // Text search option (now includes date range if found)
                    const newSearchParams = new URLSearchParams(searchParams)
                    
                    // If date was found and remainder is empty, only set date range
                    if (dateRangeResult?.date_found && !dateRangeResult.remainder?.trim()) {
                        // Only date range, no text search
                        setTextSearchQuery('')
                        newSearchParams.set('start_date', dateRangeResult.start_date!)
                        newSearchParams.set('end_date', dateRangeResult.end_date!)
                    } else if (dateRangeResult?.date_found && dateRangeResult.remainder?.trim()) {
                        // Both text search and date range
                        setTextSearchQuery(dateRangeResult.remainder)
                        newSearchParams.set('text_search', dateRangeResult.remainder)
                        newSearchParams.set('start_date', dateRangeResult.start_date!)
                        newSearchParams.set('end_date', dateRangeResult.end_date!)
                        newSearchParams.set('date_remainder', dateRangeResult.remainder)
                    } else {
                        // No date found, just text search
                        setTextSearchQuery(filterQuery)
                        newSearchParams.set('text_search', filterQuery)
                    }
                    
                    setSearchParams(newSearchParams)
                    setFilterQuery('')
                    setShowFilterDropdown(false)
                    setSelectedItemIndex(-1)
                } else if (selectedItemIndex > 0 && selectedItemIndex <= filteredItems.length) {
                    // Filter item
                    const item = filteredItems[selectedItemIndex - 1]
                    handleFilterSelect(item.type, item.item)
                    setFilterQuery('')
                    setShowFilterDropdown(false)
                    setSelectedItemIndex(-1)
                } else if (filterQuery && filteredItems.length === 0) {
                    // Fallback: submit as text search if no filter results
                    const newSearchParams = new URLSearchParams(searchParams)
                    
                    // If date was found and remainder is empty, only set date range
                    if (dateRangeResult?.date_found && !dateRangeResult.remainder?.trim()) {
                        // Only date range, no text search
                        setTextSearchQuery('')
                        newSearchParams.set('start_date', dateRangeResult.start_date!)
                        newSearchParams.set('end_date', dateRangeResult.end_date!)
                    } else if (dateRangeResult?.date_found && dateRangeResult.remainder?.trim()) {
                        // Both text search and date range
                        setTextSearchQuery(dateRangeResult.remainder)
                        newSearchParams.set('text_search', dateRangeResult.remainder)
                        newSearchParams.set('start_date', dateRangeResult.start_date!)
                        newSearchParams.set('end_date', dateRangeResult.end_date!)
                        newSearchParams.set('date_remainder', dateRangeResult.remainder)
                    } else {
                        // No date found, just text search
                        setTextSearchQuery(filterQuery)
                        newSearchParams.set('text_search', filterQuery)
                    }
                    
                    setSearchParams(newSearchParams)
                    setFilterQuery('')
                    setShowFilterDropdown(false)
                    setSelectedItemIndex(-1)
                }
                break
            case 'Escape':
                setShowFilterDropdown(false)
                setSelectedItemIndex(-1)
                break
        }
    }

    // Opening the disclosure puts the caret in it — the point of the click was
    // to search. Collapsing deliberately leaves any applied filters alone; the
    // chips below own removing those.
    useEffect(() => {
        if (showSearch) searchInputRef.current?.focus()
    }, [showSearch])

    // Handle clicking outside dropdowns
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
                setShowFilterDropdown(false)
                setSelectedItemIndex(-1)
            }
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
                setShowSortDropdown(false)
            }
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
                setShowActionMenu(false)
            }
            if (bulkActionMenuRef.current && !bulkActionMenuRef.current.contains(event.target as Node)) {
                setShowBulkActionMenu(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    // Handle filter selection
    const handleFilterSelect = (type: 'feature' | 'project' | 'collection_item', item: Feature | Project | CollectionItem) => {
        const newSearchParams = new URLSearchParams(searchParams)

        if (type === 'feature') {
            newSearchParams.set('feature', item.id.toString())
        } else if (type === 'project') {
            newSearchParams.set('project', item.id.toString())
        } else if (type === 'collection_item') {
            newSearchParams.set('collection_item', item.id.toString())
        }

        setSearchParams(newSearchParams)
        setFilterQuery('')
        setShowFilterDropdown(false)
    }

    // Clear filters
    const clearFilters = () => {
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('feature')
        newSearchParams.delete('project')
        newSearchParams.delete('collection_item')
        newSearchParams.delete('text_search')
        newSearchParams.delete('start_date')
        newSearchParams.delete('end_date')
        newSearchParams.delete('date_remainder')
        setSearchParams(newSearchParams)
        setFilterQuery('')
        setTextSearchQuery('')
        setShowFilterDropdown(false)
    }

    // Helper functions for individual filter removal
    const removeFeatureFilter = () => {
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('feature')
        setSearchParams(newSearchParams)
    }

    const removeProjectFilter = () => {
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('project')
        setSearchParams(newSearchParams)
    }

    const removeCollectionItemFilter = () => {
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('collection_item')
        setSearchParams(newSearchParams)
    }

    // Helper function to prepare payload for API
    const prepareCheckinPayload = (checkinData: ComposerCheckin): CheckinCreate => {
        return {
            body: checkinData.body,
            applies_to: checkinData.applies_to,
            // Echoed back on every save, not only when the user touched it.
            // `applies_at` absent means "no time" server-side, so a payload
            // that hand-lists its fields and forgets this one clears the time
            // on an edit that had nothing to do with it.
            applies_at: checkinData.applies_at ?? null,
            // Echoed for the same reason, one step further: a dropped
            // precision does not clear anything, it *upgrades* the row's claim
            // about its own time.
            applies_precision: checkinData.applies_precision,
            features: checkinData.features,
            projects: checkinData.projects,
            // Says which parser output these features are an echo of. The
            // server ignores our parser-owned rows when a parse has landed
            // since — see update_checkin_core.
            base_parse_generation: checkinData.parse_generation
        }
    }

    // Debounced auto-save function with no composerCheckin dependency
    const queueCheckinSave = useCallback(
        debounce(async (checkinId: number, checkinData: ComposerCheckin) => {
            // Don't save empty checkins
            if (!checkinData.body.trim() && checkinData.features.length === 0 && checkinData.projects.length === 0) {
                return
            }

            // `checkinData` was captured at keystroke time, and the 300ms
            // debounce routinely spans a parse landing (parses run 5-15s while
            // the user keeps typing). Sending that snapshot asks the server to
            // delete every row the parse just wrote. Rebuild from the current
            // state instead: the parse merged its rows into `checkins` through
            // mergeWithUserEdits, which already protected anything the user
            // touched mid-parse, so only the body/date typed since need to be
            // carried over. The server refuses a stale payload's parser rows
            // regardless (base_parse_generation) — this keeps well-formed saves
            // from needing that backstop.
            const latest = checkinsRef.current.find(c => c.id === checkinId)
            if (latest && latest.parse_generation !== checkinData.parse_generation) {
                checkinData = {
                    ...getComposerCheckinFromCheckin(latest),
                    body: checkinData.body,
                    applies_to: checkinData.applies_to,
                    // Carried over with the date for the same reason: these
                    // are the fields the user can have typed since the parse
                    // started, and the rebuilt payload is what gets sent.
                    applies_at: checkinData.applies_at ?? null,
                    applies_precision: checkinData.applies_precision
                }
            }

            setSavingState('saving')
            const savePromise = saveCheckin(checkinId, checkinData).finally(() => {
                if (pendingSavesRef.current.get(checkinId) === savePromise) {
                    pendingSavesRef.current.delete(checkinId)
                }
            })
            pendingSavesRef.current.set(checkinId, savePromise)
            await savePromise

            // A parse that started while this PUT was in flight has already
            // claimed the indicator -- clearing it here would blank the label
            // out from under it.
            setSavingState(prev => (prev === 'saving' ? null : prev))
        }, AUTO_SAVE_DELAY_MS),
        [])

    // Device location for the place-lookup anchor. Captured at the first
    // debounced parse -- the moment the user has actually committed to writing
    // something -- rather than on focus, and gated behind a one-time primer so
    // the browser's own dialog never appears unexplained. See
    // hooks/useDeviceLocation for every reason it declines to prompt.
    const locationPrimer = useOnceEver(UX_MOMENT_KEYS.locationPermissionPrimer)
    const [primerOpen, setPrimerOpen] = useState(false)
    // Resolves once the user answers the primer, so the capture can await it.
    const primerAnswer = useRef<((ok: boolean) => void) | null>(null)
    const primerStatusRef = useRef(locationPrimer.status)
    primerStatusRef.current = locationPrimer.status

    // Whether the browser may be prompted right now. Held in a ref rather than
    // derived from `locationPrimer.status`, because accepting the primer has to
    // take effect before the capture that follows it in the same tick -- going
    // through React state would make that depend on a re-render landing in
    // between. Seeded from the stored moment; set directly on accept.
    const mayPromptRef = useRef(false)
    if (locationPrimer.status === 'seen') mayPromptRef.current = true

    const requestPrimerConsent = useCallback(() => mayPromptRef.current, [])

    const { ensureFreshFix } = useDeviceLocation(requestPrimerConsent)

    /** Show the primer and resolve with the user's answer. */
    const askPrimer = useCallback(
        () => new Promise<boolean>((resolve) => {
            primerAnswer.current = resolve
            setPrimerOpen(true)
        }),
        [],
    )

    const handlePrimerAccept = useCallback(async () => {
        setPrimerOpen(false)
        mayPromptRef.current = true
        await locationPrimer.markSeen()
        primerAnswer.current?.(true)
        primerAnswer.current = null
    }, [locationPrimer])

    const handlePrimerDecline = useCallback(async () => {
        setPrimerOpen(false)
        mayPromptRef.current = false
        await locationPrimer.markSeen()
        // Durable, not a deferral -- see the modal's note. Re-asking every
        // session is the annoyance this design exists to avoid.
        try { await setDeviceLocationPref(true) } catch { /* best effort */ }
        primerAnswer.current?.(false)
        primerAnswer.current = null
    }, [locationPrimer])

    /** Capture a fix if one is due, showing the primer first if it's never
     *  been shown. Bounded so a pending permission dialog can't hold up the
     *  parse -- a fix that lands late is picked up by the next one. */
    const captureLocation = useCallback(
        async (appliesTo?: string | null) => {
            if (primerStatusRef.current === 'unseen') {
                const accepted = await askPrimer()
                if (!accepted) return
            }
            await ensureFreshFix(appliesTo)
        },
        [askPrimer, ensureFreshFix],
    )

    // The debounced parse is created once with empty deps, so it reads the
    // capture through a ref rather than closing over a stale callback.
    const captureLocationRef = useRef(captureLocation)
    captureLocationRef.current = captureLocation

    // The parse itself. Scheduled by queueAIParse below through two timers,
    // never called directly.
    const runAIParse = useCallback(
        async (checkinId: number, content: string, features?: CheckinFeature[], appliesTo?: string | null) => {
            // Claim this invocation's place in line before any await, so a
            // later one can supersede it while it waits.
            const mySeq = (parseRequestSeqRef.current.get(checkinId) ?? 0) + 1
            parseRequestSeqRef.current.set(checkinId, mySeq)
            const superseded = () => parseRequestSeqRef.current.get(checkinId) !== mySeq

            // The wait is over however this call ends -- parsed, skipped or
            // superseded -- so the countdown comes down now. A later edit
            // arms a fresh one.
            armedParseTimerRef.current = null
            setParseCountdown(null)

            // Say so in the same breath. The work below starts with waits --
            // the in-flight PUT, a previous parse, the location fix -- that can
            // run to a second or more before the request is even sent, and
            // announcing the parse only at the request left the label blank for
            // all of it, right after the user asked for a parse. From here on
            // this invocation owns the indicator until `release` hands it back.
            //
            // A call that turns out to have nothing to do releases it again
            // below. That is not a flash: with no PUT and no parse to wait for,
            // the claim and the release fall in the same tick and React batches
            // them away, which is the ordinary case for the backstop timer
            // firing over an already-parsed body.
            setSavingState('ai-parsing')
            setAnalyzingCheckinIds(prev => new Set(prev).add(checkinId))
            // The activity record is claimed at commit-to-parse below, not
            // here. This invocation may never parse at all -- it can find the
            // body already parsed, or hand over to a newer one -- and claiming
            // a tracker supersedes whatever tracker is holding the watch of a
            // parse that is still in flight and that this call is about to
            // *wait for*. Closing that watch leaves the parse running on the
            // server with nothing here hearing how it went. Until the claim,
            // the panel shows the record of the
            // parse actually running, or the plain indicator when there is
            // none.
            let tracker: ParseTracker | null = null
            // When the indicator went up, which is what MIN_VISIBLE_MS is
            // measured against -- not when the request was sent, which can be
            // seconds later.
            const claimedAt = Date.now()

            // Only ever clears what this invocation put up: a newer parse or a
            // later save may have claimed the indicator in the meantime, and
            // theirs outlives ours.
            let handedOver = false
            const release = () => {
                setAnalyzingCheckinIds(prev => {
                    const next = new Set(prev)
                    next.delete(checkinId)
                    return next
                })
                setSavingState(prev => (prev === 'ai-parsing' ? null : prev))
            }

            try {
                // Wait for any in-flight PUT for this checkin to finish before
                // parsing. The parse GCs orphan checkin_auto collection_items
                // and races with the PUT's INSERTs on cf.reference_value otherwise.
                const pending = pendingSavesRef.current.get(checkinId)
                if (pending) {
                    await pending
                }

                // Serialize parses per checkin: if a previous parse for this checkin
                // is still in flight, let it finish (and commit its items) before
                // starting another, so this one references those rows instead of
                // racing into a duplicate.
                const inflightParse = pendingParsesRef.current.get(checkinId)
                if (inflightParse) {
                    try { await inflightParse } catch { /* prior parse surfaced its own error */ }
                }

                // Both awaits above can span several seconds. If the user kept
                // typing through them a newer invocation is already queued with a
                // newer body; parsing this one would spend a full parse to answer
                // a question that has already moved on. That newer invocation
                // claimed the indicator at its own top, so leave it be -- it is
                // reporting the parse that matters now.
                if (superseded()) {
                    handedOver = true
                    return
                }

                // Nothing to learn from re-parsing byte-identical prose. Compared
                // after the awaits so it sees the body the in-flight parse just
                // committed, which is what makes the feature-edit path (which
                // queues a parse without touching the body) cheap.
                const normalizedBody = normalizeBody(content)
                if (isAlreadyParsed(lastParsedBodyRef.current.get(checkinId), content)) {
                    return
                }

                if (checkinId !== null && features) {
                    // First debounce timeout: the user has committed to writing.
                    // Capped so a pending permission dialog never blocks the parse.
                    await Promise.race([
                        captureLocationRef.current(appliesTo),
                        new Promise((r) => setTimeout(r, 1500)),
                    ])

                    // Committed: from here this invocation owns a parse, and
                    // therefore a socket and a record.
                    tracker = beginParseRef.current(checkinId)
                    const parsePromise = parseCheckinWithAI(checkinId, content, claimedAt, tracker)
                        .then(() => {
                            // Only on success: a failed parse learned nothing, so
                            // the next attempt on this body must not be skipped.
                            lastParsedBodyRef.current.set(checkinId, normalizedBody)
                        })
                        .finally(() => {
                            if (pendingParsesRef.current.get(checkinId) === parsePromise) {
                                pendingParsesRef.current.delete(checkinId)
                            }
                        })
                    pendingParsesRef.current.set(checkinId, parsePromise)
                    await parsePromise
                }
            } finally {
                // Closes this parse's socket and keeps its record, whichever
                // way the call ended. Null when this invocation never got as
                // far as parsing.
                tracker?.end()
                // Covers the ordinary end, every early return above, and a
                // parse that throws -- which used to leave the indicator
                // spinning for good.
                if (!handedOver) release()
            }
        },
        []
    )

    // Two timers, both reset by every edit.
    //
    // `parseOnIdle` is the responsive one and fires after a short pause, but
    // queueAIParse only arms it when the parse has something to chew on: the
    // first parse of a checkin, or enough new prose since the last one. A
    // parse costs the same whether the body grew by 20 characters or 500 --
    // 85% of its prompt is the projects/features/collections context, not the
    // body -- so firing one per typing pause buys almost nothing.
    //
    // `parseWhenSettled` is the backstop and fires once typing has actually
    // stopped, so the committed body is always parsed even if the last edit
    // was a two-word fix.
    const parseOnIdle = useMemo(() => debounce(runAIParse, AI_PARSE_IDLE_MS), [runAIParse])
    const parseWhenSettled = useMemo(() => debounce(runAIParse, AI_PARSE_SETTLED_MS), [runAIParse])

    const queueAIParse = useCallback(
        (checkinId: number, content: string, features?: CheckinFeature[], appliesTo?: string | null) => {
            parseWhenSettled(checkinId, content, features, appliesTo)

            const lastParsed = lastParsedBodyRef.current.get(checkinId)
            const soon = hasSubstantialNewText(lastParsed, content, AI_PARSE_MIN_NEW_CHARS)
            if (soon) {
                parseOnIdle(checkinId, content, features, appliesTo)
            }

            // Both timers restart on every edit, so the one that will actually
            // fire is whichever is armed with the shorter wait.
            armedParseTimerRef.current = soon ? 'idle' : 'settled'
            const armedAt = Date.now()
            setParseCountdown({
                checkinId,
                armedAt,
                dueAt: armedAt + (soon ? AI_PARSE_IDLE_MS : AI_PARSE_SETTLED_MS),
            })
        },
        [parseOnIdle, parseWhenSettled]
    )

    /** Skip the remaining wait and parse now. Cancels the timer that isn't
     *  firing so the parse still happens exactly once.
     *
     *  The save goes first. runAIParse waits on any in-flight PUT before it
     *  touches the checkin, but a body typed moments ago is still sitting in
     *  the 300ms auto-save debounce, so there would be nothing for it to wait
     *  on -- and the PUT would land in the middle of the parse, which is the
     *  race that guard exists to prevent. Flushing here puts the save in
     *  `pendingSavesRef` before the parse looks. Parsing now without
     *  committing now makes no sense for any caller anyway. */
    const flushAIParse = useCallback(() => {
        queueCheckinSave.flush()
        if (armedParseTimerRef.current === 'idle') {
            parseWhenSettled.cancel()
            parseOnIdle.flush()
        } else {
            parseOnIdle.cancel()
            parseWhenSettled.flush()
        }
    }, [parseOnIdle, parseWhenSettled, queueCheckinSave])

    /** The composer's close button. A parse still counting down belongs to a
     *  composer that is about to leave the screen, so take it over and run it
     *  now rather than making the user wait out a timer they can no longer
     *  see. Deletes clear `editingCheckinId` by their own paths, which is
     *  what keeps this from parsing a checkin that no longer exists. */
    const closeComposer = useCallback(() => {
        if (armedParseTimerRef.current) {
            flushAIParse()
        }
        setEditingCheckinId(null)
    }, [flushAIParse])


    // Save a new checkin
    const saveNewCheckin = async (checkinData: ComposerCheckin) => {
        // It looks like we're already creation a new checkin, but
        // haven't returned yet with an ID. Bail.
        if (!allowSaveNewCheckinRef.current) {
            return
        }
        allowSaveNewCheckinRef.current = false
        try {
            const parseStartTime = Date.now()
            const payload = prepareCheckinPayload(checkinData)
            const savedCheckin = await createCheckin(payload)

            // Merge save result with any user edits made during saving
            const mergedCheckin = mergeWithUserEdits(
                NEW_CHECKIN_ID_PLACEHOLDER,
                savedCheckin,
                parseStartTime,
            )

            // Update the checkins list with the new checkin
            setCheckins(prev =>
                prev.map(c => c.id === NEW_CHECKIN_ID_PLACEHOLDER ? mergedCheckin : c)
            )
            // Carry the composer's stable React key over to the saved id so
            // the CheckinItem doesn't unmount on the id flip (focus survives).
            const placeholderKey = slotKeyRef.current.get(NEW_CHECKIN_ID_PLACEHOLDER)
            if (placeholderKey != null) {
                slotKeyRef.current.delete(NEW_CHECKIN_ID_PLACEHOLDER)
                slotKeyRef.current.set(mergedCheckin.id, placeholderKey)
            }
            setEditingCheckinId(mergedCheckin.id)

            // We don't queue any changes between the initiation of the creation
            // and this point here, as we have no ID yet.
            // Send any such changes now.
            queueCheckinSave(mergedCheckin.id, getComposerCheckinFromCheckin(mergedCheckin))

            // Trigger AI parsing for the newly created checkin if it has content
            if (mergedCheckin.body.trim()) {
                queueAIParse(
                    mergedCheckin.id,
                    mergedCheckin.body,
                    mergedCheckin.checkin_features,
                    mergedCheckin.applies_to
                )
            }

            setShowNewCheckinComposer(false)
            
            // Check for updates after saving
            checkForUpdates()
        } catch (err) {
            notify.error(extractDetail(err) ?? 'Failed to save checkin')
            console.error(err)
            throw err
        }
    }

    // Save changes to an existing checkin
    const saveEditedCheckin = async (checkinId: number, checkinData: ComposerCheckin) => {
        try {
            const parseStartTime = Date.now()

            const payload = prepareCheckinPayload(checkinData)
            const updatedCheckin = await updateCheckin(checkinId, payload)

            // Merge save result with any user edits made during saving
            const mergedCheckin = mergeWithUserEdits(
                checkinId,
                updatedCheckin,
                parseStartTime,
            )

            // Update the checkins list with the updated checkin
            setCheckins(prev =>
                prev.map(c => c.id === checkinId ? mergedCheckin : c)
            )
            
            // Check for updates after saving
            checkForUpdates()
            
        } catch (err) {
            notify.error(extractDetail(err) ?? 'Failed to update checkin')
            console.error(err)
            throw err
        }
    }

    // Handle edit checkin button click
    const handleEditCheckin = (checkin: Checkin) => {
        setEditingCheckinId(checkin.id)
    }

    
    const saveCheckin = async (checkinId: number, checkinData: ComposerCheckin) => {
        if (checkinId == null && !checkinData.body.trim() && checkinData.features.length === 0) {
            return
        }
        
        try {
            if (checkinId === NEW_CHECKIN_ID_PLACEHOLDER) {
                await saveNewCheckin(checkinData)
            } else {
                await saveEditedCheckin(checkinId, checkinData)
            }
        } catch (err) {
            // Error is already handled in the save functions
        }
    }

    /* ---------------------------------------------------------------------
     * Drag to reorder — retiming a checkin by the midpoint of its neighbours
     * ------------------------------------------------------------------- */

    // Reordering is only meaningful where the list *is* a chronology.
    // `most_relevant` ranks by match, so a position in it says nothing about
    // time and a drag would have no time to compute.
    const reorderEnabled = sortOrder === 'most_recent' || sortOrder === 'oldest'
    // Rows and day dividers in display order. The sortable list, the dividers
    // on screen and the drop handler all read this one sequence.
    const displaySlots = useMemo(
        () => (reorderEnabled ? timelineSlots(checkins, sortOrder === 'oldest') : []),
        [checkins, reorderEnabled, sortOrder],
    )

    const reorderSensors = useSensors(
        // A few pixels of travel before a drag arms, so a tap on the grip is
        // still a tap and the row underneath keeps its own click handlers.
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        // Touch needs its own sensor with a *delay*, not a distance. On a
        // phone the distance constraint loses the race: the browser starts
        // its own long-press text selection during the 5px of travel before
        // the drag arms, so holding the grip selected the body instead of
        // picking the row up. A hold arms the drag before the selection
        // gesture resolves; `tolerance` lets a thumb wobble without
        // cancelling it. `touch-none select-none` on the grip is the other
        // half — `touch-action` alone never suppressed selection.
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    )

    /** Show the new time on a row for a moment, then let it go. */
    const flashCheckinTime = useCallback((checkinId: number, appliesAt: string) => {
        setTimeFlashes(prev => ({ ...prev, [checkinId]: appliesAt }))
        const running = timeFlashTimers.current.get(checkinId)
        if (running) window.clearTimeout(running)
        timeFlashTimers.current.set(checkinId, window.setTimeout(() => {
            timeFlashTimers.current.delete(checkinId)
            setTimeFlashes(prev => {
                const next = { ...prev }
                delete next[checkinId]
                return next
            })
        }, TIME_FLASH_MS))
    }, [])

    useEffect(() => {
        const timers = timeFlashTimers.current
        return () => { timers.forEach(t => window.clearTimeout(t)) }
    }, [])

    const handleReorderDragEnd = async (event: DragEndEvent) => {
        setReorderDragging(false)
        const { active, over } = event
        if (!over || active.id === over.id) return

        // Read the drop off the same sequence the list rendered — rows and
        // day dividers — so a drop onto a divider's place says which side of
        // the line the row went.
        const before = checkinsRef.current
        const ascending = sortOrder === 'oldest'
        const slots = timelineSlots(before, ascending)
        const from = slots.findIndex(s => slotId(s) === active.id)
        const to = slots.findIndex(s => slotId(s) === over.id)
        if (from < 0 || to < 0) return
        const movedRow = slots[from]
        if (isDayBoundary(movedRow)) return
        const movedId = movedRow.id

        // The rule is read off the order the drop *produced*, converted to
        // chronological order inside `dates.ts`. Nothing here knows or cares
        // which way the screen runs.
        const dropped = arrayMove(slots, from, to)
        const day = dropDay(dropped, movedId, ascending)
        if (!day) return
        const moved = dropped.filter((s): s is Checkin => !isDayBoundary(s))
        const movesDay = dayOf(movedRow) !== day
        const movedDay = moved.filter(c => c.id === movedId || dayOf(c) === day)

        // Dropped between two checkins that both have no time, the midpoint is
        // 00:00 — where they already are — so the drag would appear not to
        // stick. Ask for the day's times instead of writing a meaningless one.
        // The drag itself is left undone: the list snaps back and what gets
        // written is whatever the user says in the dialog.
        if (dropNeedsTimeAssignment(movedDay, movedId, ascending)) {
            setTimeAssignment({
                day,
                rows: movedDay.map(c => ({
                    id: c.id,
                    body: c.body,
                    applies_at: c.applies_at ?? null,
                    applies_precision: c.applies_precision,
                })),
                placingId: movedId,
                movesDay,
            })
            return
        }

        const appliesAt = retimeForReorder(movedDay, movedId, ascending)
        if (!appliesAt) return

        setCheckins(() => applyLocalRetime(moved, day, movedId, appliesAt, ascending))
        flashCheckinTime(movedId, appliesAt)

        try {
            // `inferred`: this time is arithmetic on two neighbours, not
            // something the user said. It is the one write in the product that
            // must not be counted as user-set, and the route requires it to be
            // stated rather than defaulting.
            const saved = await retimeCheckin(
                movedId, appliesAt, REORDER_PRECISION, movesDay ? day : undefined,
            )
            setCheckins(prev => prev.map(c => c.id === movedId
                ? {
                    ...c,
                    applies_to: saved.applies_to,
                    applies_at: saved.applies_at ?? null,
                    applies_precision: saved.applies_precision,
                    updated_at: saved.updated_at,
                }
                : c))
        } catch (err) {
            setCheckins(() => before)
            notify.error('Could not move that checkin. Please try again.')
        }
    }

    /**
     * Write the times the dialog collected, in one request.
     *
     * Nothing is written optimistically: the whole point of the batch is that
     * the day either takes all of these times or none of them, so the rows on
     * screen come from what the server actually stored. A failure throws back
     * into the dialog, which stays open with the user's times still in it.
     */
    const handleTimeAssignmentConfirm = async (changes: CheckinTimeAssignment[]) => {
        if (!timeAssignment) return
        const { day, placingId, movesDay } = timeAssignment
        const saved = await retimeCheckins(changes.map(c => (
            movesDay && c.checkin_id === placingId ? { ...c, applies_to: day } : c
        )))
        const byId = new Map(saved.map(c => [c.id, c]))
        setCheckins(prev => {
            const updated = prev.map(c => {
                const row = byId.get(c.id)
                return row
                    ? {
                        ...c,
                        applies_to: row.applies_to,
                        applies_at: row.applies_at ?? null,
                        applies_precision: row.applies_precision,
                        updated_at: row.updated_at,
                    }
                    : c
            })
            // Re-sort the way the server would, so the order the user just
            // described is the order they see.
            return movesDay
                ? sortTimelineByDay(updated, sortOrder === 'oldest')
                : resortDay(updated, day, sortOrder === 'oldest')
        })
        setTimeAssignment(null)
    }

    // Helper function to mark a field as edited with the current timestamp
    const handleFieldEdit = (checkinId: number, field: 'body' | 'date' | 'time' | 'feature' | 'project', checkin: ComposerCheckin, featureId?: number, projectId?: number) => {
        const now = Date.now()

        // Get or initialize edit timestamps for this checkin
        let checkinTimestamps = editTimestampsRef.current.get(checkinId)
        if (!checkinTimestamps) {
            checkinTimestamps = {
                body: 0,
                features: new Map<number, number>(),
                projects: new Map<number, number>()
            }
            editTimestampsRef.current.set(checkinId, checkinTimestamps)
        }

        // Update the appropriate timestamp. Projects get their own per-id map:
        // mergeWithUserEdits keys project protection off it, so a project the
        // user removed mid-parse survives the AI response.
        if (field === 'project' && projectId !== undefined) {
            checkinTimestamps.body = now
            checkinTimestamps.projects.set(projectId, now)
        } else if (field === 'body' || field === 'date' || field === 'time' || field === 'project') {
            checkinTimestamps.body = now
        } else if (field === 'feature' && featureId !== undefined) {
            checkinTimestamps.features.set(featureId, now)
        }

        queueCheckinSave(checkinId, checkin)

        // If editing an existing checkin, also trigger AI parsing
        if (checkinId !== NEW_CHECKIN_ID_PLACEHOLDER) {
            // For body edits, check if the content has meaningfully changed (not just whitespace)
            let shouldParseAI = true
            // Setting a time of day says nothing about the body, and the
            // parser has no opinion about `applies_at`, so re-running it would
            // be an LLM call bought with nothing.
            if (field === 'time') {
                shouldParseAI = false
            }
            if (field === 'body') {
                const currentCheckin = checkinsRef.current.find(c => c.id === checkinId)
                const oldBodyNormalized = currentCheckin?.body?.trim().replace(/\s+/g, ' ') || ''
                const newBodyNormalized = checkin.body.trim().replace(/\s+/g, ' ')
                
                // Skip AI parsing if only whitespace changed
                if (oldBodyNormalized === newBodyNormalized) {
                    shouldParseAI = false
                }
            }
            
            if (shouldParseAI) {
                queueAIParse(
                    checkinId,
                    checkin.body,
                    checkin.features,
                    checkin.applies_to
                )
            }
        }

        // Update the checkins list with the current checkin data for existing checkins
        setCheckins(prev => {
            return prev.map(c => {
                if (c.id === checkinId) {
                    // Convert composer checkin to API checkin format
                    return {
                        ...c,
                        body: checkin.body,
                        applies_to: checkin.applies_to,
                        applies_at: checkin.applies_at ?? null,
                        applies_precision: checkin.applies_precision,
                        checkin_features: checkin.features,
                        checkin_projects: checkin.projects
                    }
                }
                return c
            })
        })
    }
    
    // Helper function to merge AI changes with user edits made during parse
    const mergeWithUserEdits = (
        checkinId: number,
        serverResponse: any, 
        parseStartTime: number
    ): Checkin => {
        // Get edit timestamps for this checkin
        const checkinTimestamps = editTimestampsRef.current.get(checkinId)
        if (!checkinTimestamps) {
            // No edits were made during parsing, return server response as is
            return serverResponse
        }
        
        // Check if body was edited during parse
        const bodyEditTime = checkinTimestamps.body
        const bodyToUse = bodyEditTime > parseStartTime
            ? checkinsRef.current.find(c => c.id === checkinId)?.body || serverResponse.body
            : serverResponse.body

        // Build new features array, preserving any that were edited during parse
        const newFeatures = [...serverResponse.checkin_features]
        const editTimeMap = checkinTimestamps.features
        
        // Get current checkin
        const currentCheckin = checkinsRef.current.find(c => c.id === checkinId)
        if (!currentCheckin) {
            // No current checkin found, return server response with updated body
            return {
                ...serverResponse,
                body: bodyToUse
            }
        }
        
        // Note that the user can't remove anything. They only add manual_overrides.
        // For each feature in the current checkin
        currentCheckin.checkin_features.forEach(feature => {
            const featureId = feature.feature_id
            const featureEditTime = editTimeMap.get(featureId) || 0
            
            // If this feature was edited after parse started, use the current value
            if (featureEditTime > parseStartTime) {
                // Find and replace this feature in newFeatures or add it
                const featureIndex = newFeatures.findIndex(f => f.feature_id === featureId)
                if (featureIndex >= 0) {
                    newFeatures[featureIndex] = feature
                } else {
                    newFeatures.push(feature)
                }
            }
        })

        // Determine which projects to keep after AI parsing
        const newProjects = [...serverResponse.checkin_projects || []]
        const currentProjects = currentCheckin.checkin_projects || []
        const projectEditTimeMap = checkinTimestamps.projects

        currentProjects.forEach(project => {
            const projectId = project.project_id
            const projectEditTime = projectEditTimeMap.get(projectId) || 0
            
            // If this project was edited after parse started, use the current value
            if (projectEditTime > parseStartTime) {
                // Find and replace this project in newProjects or add it
                const projectIndex = newProjects.findIndex(p => p.project_id === projectId)
                if (projectIndex >= 0) {
                    newProjects[projectIndex] = project
                } else {
                    newProjects.push(project)
                }
            }
        }
        )
        
        // Create merged checkin
        return {
            ...serverResponse,
            body: bodyToUse,
            checkin_features: newFeatures,
            checkin_projects: newProjects
        }
    }

    // Parse checkin content with AI (invoked by CheckinComposer through onParseWithAI)
    const parseCheckinWithAI = async (
        checkinId: number, content: string,
        claimedAt: number = Date.now(), tracker?: ParseTracker) => {
        if (!content.trim()) {
            return null
        }

        try {
            // Capture parse start timestamp for conflict resolution
            const parseStartTime = Date.now()
            
            const onStageAComplete = (detectedProjectIds: number[], statusWords: string[]) => {
                // Stage A complete - update UI with detected projects,
                // chips included. See `applyStageAProjects`.
                // The flavor words go to the panel through the tracker, so
                // a superseded parse's words cannot land on a newer one.
                tracker?.onStatusWords(statusWords)
                setCheckins(prev =>
                    prev.map(c =>
                        c.id === checkinId
                            ? applyStageAProjects(c, detectedProjectIds, projectsRef.current)
                            : c
                    )
                )
            }
            // The panel's feed, and the handle the hook closes the socket
            // through when a newer parse supersedes this one.
            const feed = tracker && { onActivity: tracker.onActivity, onSocket: tracker.onSocket }
            const parseResponse = await parseCheckinViaJob(checkinId, content, 'composer', {
                ...feed,
                onStageAComplete,
            })

            // Merge AI results with any user edits made during parsing
            const mergedCheckin = mergeWithUserEdits(
                checkinId,
                parseResponse.checkin,
                parseStartTime
            )

            // Pull any newly-referenced collection items into the local
            // cache. Without this, references the parser added (e.g. a
            // resolver-bootstrapped Place, a CREATE_NEW reference item)
            // would render with no name until the next full fetchData.
            mergeCollectionItems(parseResponse.collection_items)

            // Same idea for feature definitions: the parser may lazily
            // bootstrap a system feature (e.g. user-global Locations on the
            // first location parse). The composer's chip lookup goes through
            // `[...recentFeatures, ...features]`, so without this merge a
            // freshly-created feature's chip silently doesn't render until
            // the next fetchData.
            mergeFeatures(parseResponse.checkin.feature_details || [])

            // Update the checkins list with the merged checkin
            setCheckins(prev =>
                prev.map(c => c.id === checkinId ? mergedCheckin : c)
            )

            // Check for updates after AI parsing
            checkForUpdates()

            // Hold the indicator to its floor, if it hasn't already earned it.
            // The old unconditional 300ms pad charged every parse for the one
            // case that needs it; a parse that has been running for seconds
            // has been visible all along and should end with its chips, not a
            // third of a second after them.
            const shown = Date.now() - claimedAt
            if (shown < MIN_VISIBLE_MS) {
                await new Promise(resolve => setTimeout(resolve, MIN_VISIBLE_MS - shown))
            }
        } catch (err) {
            const quota = extractQuotaError(err)
            if (quota) {
                setQuotaError(quota)
            } else {
                notify.error('Failed to parse checkin with AI')
            }
            console.error(err)
        }
    }

    // Handle delete checkin
    const handleDeleteCheckin = async (checkinId: number, force: boolean = false) => {
        if (!force) {
            setDeleteConfirmation({ isOpen: true, checkinId, isDeleting: false })
            return
        }
        
        setDeleteConfirmation(prev => ({ ...prev, isDeleting: true }))
        
        try {
            await deleteCheckin(checkinId)

            // Remove the checkin from state
            setCheckins(prev => prev.filter(c => c.id !== checkinId))

            // Clear editing state if we were editing this checkin
            if (editingCheckinId === checkinId) {
                setEditingCheckinId(null)
            }
            
            setDeleteConfirmation({ isOpen: false, checkinId: null, isDeleting: false })
        } catch (err) {
            notify.error('Failed to delete checkin')
            console.error(err)
            setDeleteConfirmation(prev => ({ ...prev, isDeleting: false }))
        }
    }

    // Handle bulk delete checkins
    const handleBulkDeleteCheckins = async (checkinIds: number[], force: boolean = false) => {
        if (!force) {
            setDeleteConfirmation({ isOpen: true, checkinId: null, checkinIds, isDeleting: false })
            return
        }
        
        setDeleteConfirmation(prev => ({ ...prev, isDeleting: true }))
        
        try {
            await deleteCheckins(checkinIds)

            // Remove the checkins from state
            setCheckins(prev => prev.filter(c => !checkinIds.includes(c.id)))

            // Clear editing state if we were editing any of these checkins
            if (checkinIds.includes(editingCheckinId!)) {
                setEditingCheckinId(null)
            }
            
            setDeleteConfirmation({ isOpen: false, checkinId: null, checkinIds: undefined, isDeleting: false })
            setIsSelectionMode(false)
            setSelectedCheckinIds(new Set())
            notify.success(`Successfully deleted ${checkinIds.length} checkin${checkinIds.length === 1 ? '' : 's'}`)
        } catch (err) {
            notify.error('Failed to delete checkins')
            console.error(err)
            setDeleteConfirmation(prev => ({ ...prev, isDeleting: false }))
        }
    }

    // The nudges the server is willing to show right now. Called on load, and
    // again after a Duplicate, which is the one action in this page that
    // creates one. Failures are swallowed: a missing offer is not worth an
    // error notice over the timeline the user came for.
    const refreshNudges = useCallback(() => {
        getCheckinNudges()
            .then(setNudges)
            .catch(err => console.error('Error loading checkin nudges:', err))
    }, [])

    useEffect(() => {
        refreshNudges()
    }, [refreshNudges])

    // The repetition judge, fired as an extra step AFTER the timeline has
    // loaded and rendered — never before, and never as something the list
    // waits on. It is the one part of this page that can reach a model, and
    // the user did not ask for it, so it gets no error notice, no spinner and
    // no retry: a scan that fails is indistinguishable from one that found
    // nothing.
    //
    // Once per mount. The gate is server-side (`users.prefs`
    // .repetitionWatermarkCheckinId plus the decisions table) and most calls
    // return having read two indexes, so the interesting cost is not the
    // request — it is asking again while the answer cannot have changed, which
    // the ref prevents for filter and search refetches within one visit.
    const repetitionScanned = useRef(false)
    useEffect(() => {
        if (loading || repetitionScanned.current) return
        repetitionScanned.current = true
        scanCheckinRepetition()
            .then(result => {
                // Only a scan that raised something has anything for the list
                // to show; every other outcome leaves the nudges unchanged.
                if (result.nudges_created > 0) refreshNudges()
            })
            .catch(() => {})
    }, [loading, refreshNudges])

    // Drop a freshly-created checkin in at the top of the list, read-only.
    // Shared by Duplicate and by a template tap — both produce a complete
    // checkin server-side, so neither has anything for the composer to do.
    const landNewCheckin = (checkin: Checkin) => {
        setCheckins(prev => {
            const withoutEmpty = firstElementIsEmptyCheckin(prev) ? prev.slice(1) : prev
            return [checkin, ...withoutEmpty.filter(existing => existing.id !== checkin.id)]
        })
        setShowNewCheckinComposer(false)
        setLandedCheckinId(checkin.id)
    }

    // Handle duplicate checkin.
    //
    // This used to end with `setEditingCheckinId(duplicatedCheckin.id)`, which
    // opened the copy in the composer. It no longer does: a duplicate is a
    // finished checkin, and now that a template tap lands one the same way,
    // the two should feel identical. Editing is still one menu tap away.
    const handleDuplicateCheckin = async (checkinId: number) => {
        try {
            const duplicatedCheckin = await duplicateCheckin(checkinId)
            landNewCheckin(duplicatedCheckin)
            // Duplicating raises a `template_upsell` nudge on the copy — the
            // press is itself the "I repeat this" signal, which is why it fires
            // on the first duplicate rather than waiting for a third
            // occurrence. Refetch rather than synthesize: the server decides
            // whether there is an offer to make at all (there isn't for a body
            // already saved as a template, or one already dismissed), and the
            // page has no way to know which.
            refreshNudges()
        } catch (err) {
            notify.error('Failed to duplicate checkin')
            console.error(err)
        }
    }

    // Snapshot a checkin as a one-tap template. Idempotent server-side: saving
    // something already saved returns the existing template, which the pill
    // row merges by id rather than duplicating — so there is no "already
    // saved" error to report, and the toast reads the same either way.
    const handleSaveAsTemplate = async (checkinId: number) => {
        try {
            const template = await createCheckinTemplate(checkinId)
            setSavedTemplate(template)
            notify.success(`Saved "${template.label}" as a shortcut`)
            // The server suppresses any nudge this template answers, but only
            // on the next read. Drop it here so accepting an offer makes it go
            // away at once rather than at the next page load. Both tests below
            // mirror the server's two suppression joins: a nudge whose own
            // signature is the body (`template_upsell`), and one that names in
            // its payload the signature the template would get (the repetition
            // judge, whose signature is a cluster slug and so never matches a
            // template's on its own).
            setNudges(prev => prev.filter(nudge =>
                nudge.signature !== template.signature &&
                nudge.payload?.template_signature !== template.signature
            ))
        } catch (err) {
            notify.error('Failed to save this checkin as a shortcut')
            console.error(err)
        }
    }

    // A nudge's primary button. `saveTemplate` is the only shape any kind emits
    // today; `openGenie` exists in `NudgeAction` for the parser's follow-up
    // questions, whose handler is the `discussCheckinsInGenie` path already
    // used by "Discuss in Genie" — POST, then navigate to `/genie/{id}`. It is
    // unreachable until a kind emits it, which is Stage 6's business at the
    // earliest, and is left as a warn rather than a silent no-op so a kind
    // shipped without its handler is noisy.
    const handleNudgeAction = async (nudge: CheckinNudge, action: NudgeAction) => {
        if (nudgeBusy) return
        setNudgeBusy(true)
        try {
            switch (action.type) {
                case 'saveTemplate':
                    await handleSaveAsTemplate(nudge.checkin_id)
                    break
                case 'openGenie':
                    console.warn('No nudge kind emits openGenie yet:', action.prompt)
                    break
            }
        } finally {
            setNudgeBusy(false)
        }
    }

    // Dismissal is one bit and it is permanent: the server keeps the row as the
    // block against ever raising that signature again. Dropping it locally too
    // reveals whichever nudge was queued behind it.
    const handleNudgeDismiss = async (nudge: CheckinNudge) => {
        if (nudgeBusy) return
        setNudgeBusy(true)
        try {
            await dismissCheckinNudge(nudge.id)
            setNudges(prev => prev.filter(existing => existing.id !== nudge.id))
        } catch (err) {
            notify.error('Failed to dismiss that suggestion')
            console.error(err)
        } finally {
            setNudgeBusy(false)
        }
    }

    // The way back into a parse that has already finished — the ⋮ menu's "Show
    // analysis". It reads the stored events rather than anything this page kept:
    // the live record is discarded when the indicator comes down, and after a
    // reload there was never one. Same events, same fold, same panel.
    const handleReviewParse = useCallback(async (checkinId: number) => {
        try {
            const found = await reviewParse(checkinId)
            if (!found) {
                notify.error('No saved analysis for this checkin')
            }
        } catch (err) {
            console.error('Failed to load the stored parse activity:', err)
            notify.error('Failed to load the analysis for this checkin')
        }
    }, [reviewParse, notify])

    // Handle reanalyze single checkin
    //
    // One checkin runs as the composer's does — a job, watched — so the ⋮
    // menu's "Analyze" shows the same live panel. A multi-checkin re-analyze
    // (`executeBulkReanalyze` below) queues one job per check-in instead.
    const handleReanalyzeCheckin = async (checkinId: number) => {
        setQuotaError(null)

        // Re-analyze re-runs the checkin as it stands, so the content is the
        // stored body rather than anything in a composer. The old bulk call
        // read it server-side; the job takes it from the caller, as the
        // composer's does.
        const body = checkinsRef.current.find(c => c.id === checkinId)?.body ?? ''
        if (!body.trim()) {
            notify.error('Failed to analyze checkin')
            return
        }

        setAnalyzingCheckinIds(prev => new Set(prev).add(checkinId))
        // Claimed here and not a line earlier: `beginParse` closes whatever
        // socket this checkin already had open, so a tracker taken before the
        // parse is certain to happen would kill an in-flight parse for a bail-out.
        const tracker = beginParse(checkinId)

        try {
            // Only the flavor words, which the panel's collapsed line wants.
            // Stage A's project ids are *not* applied optimistically here the
            // way the composer applies them: this path refreshes wholesale
            // below, and an optimistic chip on a closed row would be a change
            // to what re-analyze does rather than to how it gets there.
            const onStageAComplete = (_projectIds: number[], statusWords: string[]) =>
                tracker.onStatusWords(statusWords)
            // Not `composer`: this parse is a deliberate re-run of an existing
            // note, and the parse-quality corpus reads the difference off the
            // `checkin_parse_jobs` row.
            await parseCheckinViaJob(checkinId, body, 'reanalyze', {
                onActivity: tracker.onActivity,
                onSocket: tracker.onSocket,
                onStageAComplete,
            })

            notify.success('Successfully analyzed checkin')
            // The refresh re-analyze has always done. Deliberately not the
            // composer's merge: nothing here raced a user's edits, and the
            // whole row is being replaced rather than reconciled.
            await fetchData()
        } catch (err) {
            console.error('Reanalyze failed:', err)
            const quota = extractQuotaError(err)
            if (quota) {
                setQuotaError(quota)
            } else {
                notify.error('Failed to analyze checkin')
            }
        } finally {
            tracker.end()
            setAnalyzingCheckinIds(prev => {
                const newSet = new Set(prev)
                newSet.delete(checkinId)
                return newSet
            })
        }
    }

    // A multi-checkin re-analyze: one parse job per check-in, queued by one
    // request that returns at once, then each watched the way a resumed parse
    // is — its row's indicator fills while it runs and the row refreshes when
    // its own job finishes, not all of them at the end.
    const executeBulkReanalyze = async () => {
        if (selectedCheckinIds.size === 0) return
        setIsBulkParsing(true)
        setQuotaError(null)
        let started: Awaited<ReturnType<typeof bulkParseCheckins>>
        try {
            started = await bulkParseCheckins(Array.from(selectedCheckinIds))
        } catch (err) {
            notify.error('Failed to analyze checkins')
            console.error(err)
            setIsBulkParsing(false)
            return
        } finally {
            setIsSelectionMode(false)
            setSelectedCheckinIds(new Set())
            setShowReanalyzeConfirm(false)
        }

        // Every job of a sweep that hits the daily cap reports it; one banner.
        let quotaShown = false
        const outcomes = await Promise.all(started.jobs.map(async job => {
            // A check-in already being parsed came back as that parse's job.
            // If this page is watching it already (the composer, or a resumed
            // job), that watch refreshes the row; a second watch would take
            // the first one's indicator away.
            const composing = pendingParsesRef.current.get(job.checkin_id)
            if (job.deduped && composing) {
                await composing.catch(() => undefined)
                return true
            }
            const { ok, quota } = await followParseJob(job.checkin_id, job.job_id)
            if (quota && !quotaShown && mountedRef.current) {
                quotaShown = true
                setQuotaError(quota)
            }
            return ok
        }))
        if (!mountedRef.current) return
        setIsBulkParsing(false)

        const done = outcomes.filter(Boolean).length
        const failed = started.failed_checkins.length + outcomes.length - done
        const plural = (n: number) => `${n} checkin${n === 1 ? '' : 's'}`
        if (done > 0) notify.success(`Analyzed ${plural(done)}`)
        // The quota banner already explains the ones the cap stopped.
        if (failed > 0 && !quotaShown) notify.error(`Couldn't analyze ${plural(failed)}`)
    }

    const formatDate = (dateString: string, includeYearIfDifferent: boolean = false) => {
        // Parse the date string directly as YYYY-MM-DD to avoid timezone issues
        const [year, month, day] = dateString.split('-').map(Number)
        const date = new Date(year, month - 1, day) // month is 0-indexed
        
        const currentYear = new Date().getFullYear()
        const shouldIncludeYear = includeYearIfDifferent && year !== currentYear
        
        // Use toLocaleDateString with options for a more user-friendly format
        // This will automatically use the user's browser locale settings
        return date.toLocaleDateString(undefined, { 
            weekday: 'short',
            month: 'short', 
            day: 'numeric',
            ...(shouldIncludeYear && { year: 'numeric' })
        })
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spinner />
            </div>
        )
    }

    const filteredCheckins: Map<number, Checkin> = new Map(checkins.map(checkin => {
        const projects = (checkin.project_details ?? []).filter(project => {
            // Only show projects that aren't marked with DELETE override
            const cp = checkin.checkin_projects.find(cp => cp.project_id === project.id)
            return !cp || cp.manual_override !== ManualOverrideType.DELETE
        })
        const features = (checkin.feature_details ?? []).filter(feature => {
            // List features carry one checkin_features row per value, so a
            // single-`.find` DELETE check picks one row arbitrarily and can
            // hide a feature whose other rows are live (the friends-on-1468
            // bug). `hasActiveCheckinFeatureRow` checks every row.
            return hasActiveCheckinFeatureRow(checkin, feature.id)
        })
        return [checkin.id, {
            ...checkin,
            feature_details: features,
            project_details: projects
        }]
    }))

    // One nudge on screen at a time, and only when its checkin is on screen
    // too — the card is an annotation on a row, so a nudge whose anchor is off
    // the end of the list (or filtered out of it) waits rather than floating.
    // Anything queued behind it surfaces the moment this one is dismissed or
    // accepted.
    const visibleNudge = selectVisibleNudge(nudges)
    const nudgeAnchorId =
        visibleNudge && filteredCheckins.has(visibleNudge.checkin_id)
            ? visibleNudge.checkin_id
            : null
    const nudgeFor = (checkin: Checkin): React.ReactNode => {
        if (!visibleNudge || checkin.id !== nudgeAnchorId) return null
        // Not while that row is open in the composer: the user is editing the
        // very text the offer is about.
        if (editingCheckinId === checkin.id) return null
        return (
            <CheckinNudgeCard
                kind={visibleNudge.kind}
                payload={visibleNudge.payload}
                anchorBody={checkin.body}
                busy={nudgeBusy}
                onAction={action => handleNudgeAction(visibleNudge, action)}
                onDismiss={() => handleNudgeDismiss(visibleNudge)}
            />
        )
    }

    return (
        <div className="max-w-4xl mx-auto px-0 sm:px-4 pb-4 sm:py-8">
            <div className="mb-8">
                <div className="flex justify-between items-center mb-6 px-4 sm:px-0">
                    <h1 className="text-3xl font-bold text-gray-800">Checkins</h1>

                    <div className="flex items-center gap-2">
                    {/* The search disclosure. Gated on the same
                        `!isSingleCheckinView` as the bar it opens rather than on
                        the "+"'s condition, so searching stays reachable while
                        a checkin is being composed — which is how it behaved
                        when the bar was always on screen. It reads as the
                        quieter of the two controls on purpose: the "+" is the
                        page's primary action and keeps the gold.

                        `bg-gray-100` / `hover:bg-gray-200` is the app's most
                        common secondary-button idiom — it's what the Cancel
                        buttons wear — chosen because there is no secondary
                        token to reach for the way `action` and `danger` exist.
                        The open state is the same family one step darker,
                        which is what a pressed toggle should be; the bar
                        appearing below is the real state indicator, so this
                        doesn't need to announce itself. */}
                    {!isSingleCheckinView && (
                        <motion.button
                            onClick={() => setShowSearch(!showSearch)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                            className={`p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-action cursor-pointer ${
                                showSearch
                                    ? 'bg-secondary-dark text-gray-800'
                                    : 'bg-secondary text-gray-700 hover:bg-secondary-dark'
                            }`}
                            title={showSearch ? 'Hide search' : 'Search checkins'}
                            aria-label={showSearch ? 'Hide search' : 'Search checkins'}
                            aria-expanded={showSearch}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </motion.button>
                    )}

                    {!showNewCheckinComposer && !isSelectionMode && (
                        <div className="relative" ref={actionMenuRef}>
                            <RoundPlusButton
                                onClick={() => setShowActionMenu(!showActionMenu)}
                                label="Actions"
                                open={showActionMenu}
                            />

                            <AnimatePresence>
                            {showActionMenu && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95, y: -8 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: -8 }}
                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                    style={{ transformOrigin: 'top right' }}
                                    className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                    <button
                                        onClick={() => {
                                            setShowActionMenu(false)
                                            // Create initial features and projects arrays based on selected filters
                                            const initialFeatures: CheckinFeature[] = []
                                            const initialProjects: CheckinProject[] = []
                                            
                                            // If a feature filter is selected, add it to the initial features
                                            if (selectedFeatureId) {
                                                initialFeatures.push({
                                                    feature_id: selectedFeatureId,
                                                    manual_override: ManualOverrideType.KEEP_VALUE // Set to KEEP_VALUE for filter-based tagging
                                                })
                                            }
                                            
                                            // If a project filter is selected, add it to the initial projects
                                            if (selectedProjectId) {
                                                initialProjects.push({
                                                    project_id: selectedProjectId,
                                                    manual_override: ManualOverrideType.KEEP_VALUE // Set to KEEP_VALUE for filter-based tagging
                                                })
                                            }
                                            
                                            const now = new Date()
                                            const localToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
                                            const newCheckin: Checkin = {
                                                id: NEW_CHECKIN_ID_PLACEHOLDER,
                                                body: '',
                                                applies_to: localToday,
                                                // A new checkin starts untimed; the picker's
                                                // "now" is one tap away when a time matters.
                                                applies_at: null,
                                                applies_precision: 'none',
                                                checkin_features: initialFeatures,
                                                feature_details: [],
                                                checkin_projects: initialProjects,
                                                created_at: new Date().toISOString(),
                                                updated_at: new Date().toISOString(),
                                                user_id: 0
                                            }
                                            setCheckins(prev => {
                                                if (prev[0]?.id === NEW_CHECKIN_ID_PLACEHOLDER) {
                                                    return prev
                                                }
                                                return [newCheckin, ...prev]
                                            })
                                            // Pin a stable React key to this composer slot so
                                            // the eventual placeholder→real-id swap (see save
                                            // handler) doesn't unmount the CheckinItem and
                                            // kill textarea focus.
                                            slotKeyRef.current.set(
                                                NEW_CHECKIN_ID_PLACEHOLDER,
                                                `composer-${Date.now()}`,
                                            )
                                            setEditingCheckinId(null)
                                            setShowNewCheckinComposer(true)
                                        }}
                                        className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                                    >
                                        <div className="flex items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                            New Checkin
                                        </div>
                                    </button>
                                    
                                    <button
                                        onClick={() => {
                                            setShowActionMenu(false)
                                            // Build URL parameters for coaching chat
                                            const coachingParams = new URLSearchParams();
                                            
                                            // Pass current filters
                                            if (selectedFeatureId) {
                                                coachingParams.set('feature', selectedFeatureId.toString());
                                            }
                                            if (selectedProjectId) {
                                                coachingParams.set('project', selectedProjectId.toString());
                                            }
                                            if (textSearchQuery) {
                                                coachingParams.set('text_search', textSearchQuery);
                                            }
                                            if (selectedDateRange) {
                                                coachingParams.set('start_date', selectedDateRange.start_date);
                                                coachingParams.set('end_date', selectedDateRange.end_date);
                                                if (selectedDateRange.remainder) {
                                                    coachingParams.set('date_remainder', selectedDateRange.remainder);
                                                }
                                            }
                                            
                                            const queryString = coachingParams.toString();
                                            navigate(`/coaching${queryString ? '?' + queryString : ''}`);
                                        }}
                                        className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                                    >
                                        <div className="flex items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-amber-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                            </svg>
                                            Get Advice
                                        </div>
                                    </button>
                                    
                                    <button
                                        onClick={() => {
                                            setShowActionMenu(false)
                                            setIsSelectionMode(true)
                                            setSelectedCheckinIds(new Set())
                                        }}
                                        className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                                    >
                                        <div className="flex items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Select...
                                        </div>
                                    </button>
                                </motion.div>
                            )}
                            </AnimatePresence>
                        </div>
                    )}
                    </div>
                </div>

                {/* Status Messages */}
                {(isBulkParsing || hasUpdatesAvailable || quotaError) && (
                    <div className="mb-6 px-4 sm:px-0">
                        {isBulkParsing && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                                <div className="flex items-center">
                                    <div className="animate-spin h-5 w-5 border-2 border-amber-500 border-t-transparent rounded-full mr-3"></div>
                                    <p className="text-amber-900 font-medium">Analyzing checkins for structure...</p>
                                </div>
                            </div>
                        )}
                        {quotaError && (
                            <QuotaBanner
                                quotaError={quotaError}
                                onDismiss={() => setQuotaError(null)}
                            />
                        )}
                        {hasUpdatesAvailable && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                        <p className="text-yellow-800 font-medium">Updates available. Projects or features have been modified.</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => fetchData()}
                                            className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded text-sm font-medium hover:bg-yellow-200 transition-colors"
                                        >
                                            Refresh
                                        </button>
                                        <button
                                            onClick={() => setHasUpdatesAvailable(false)}
                                            className="text-yellow-600 hover:text-yellow-800"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Filters */}
                {!isSingleCheckinView && (
                <div className="mb-6 px-4 sm:px-0">
                    {/* No `gap` on this column on purpose: the search row's
                        spacing lives inside its own animated height as `pb-4`.
                        A flex gap would survive the exit — the row is still
                        mounted at height 0 while it animates out — and then
                        vanish on unmount, putting back a 16px jump of exactly
                        the kind the height animation exists to remove. */}
                    <div className="flex flex-col">
                        {/* The one-tap repeat checkins, above the search
                            disclosure rather than inside it: they're a way of
                            writing, not a way of finding, and they have to be
                            reachable whether or not the search box is open.
                            The row renders nothing when the user has no
                            templates, so it costs no space until it earns it.

                            A tap prepends to the list without clearing the
                            active filter or text search — the new checkin may
                            well not match either, and silently dropping the
                            user's search to show it would be the ruder of the
                            two surprises. */}
                        <CheckinTemplatePills
                            onApplied={landNewCheckin}
                            insertedTemplate={savedTemplate}
                            onError={notify.error}
                        />
                        {/* Search Bar Row.

                            It animates `height`, which is a layout property, so
                            the timeline below slides instead of jumping. It has
                            to be height: the opacity/scale/y vocabulary the
                            dropdowns use is compositor-only and deliberately
                            doesn't affect layout, which is what makes it cheap —
                            and also what makes it unable to move anything
                            around it. Fine for a dropdown, which is absolute and
                            out of flow with nothing below it to push; this row
                            is in flow.

                            Only `opacity` rides along, and deliberately no
                            transform. A growing box has to clip, or the
                            full-height row would spill over the list while the
                            box is still short — and a transform fights that
                            clip: `y: -8` lifts the content clean above the clip
                            edge (measured at -4px of slack), and the spring's
                            scale overshoot pushes the left edge out past it.
                            The height animation is already the movement here, so
                            the borrowed pop was redundant as well as harmful.

                            `clipSearchRow` keeps the clip on only while the
                            height is moving. Left on at rest it eats the
                            suggestions panel, which is absolutely positioned
                            below the field. And the `p-1 -m-1` gives the clip
                            box 4px of slack on every side for the focus ring:
                            the field is focused the moment the row opens, the
                            content is top-anchored in a box growing from zero,
                            so the input's top edge sits on the clip edge for the
                            whole animation while `ring-2` paints 2px outside it.
                            The negative margin takes that slack back out of the
                            layout so nothing moves.

                            The sort control rides along inside, which is where
                            it already lived — and "Most Relevant" only exists
                            while a text search does, so it belongs with the
                            search rather than beside it. */}
                        <AnimatePresence initial={false}>
                        {showSearch && (
                        <motion.div
                            key="search-bar"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                            onAnimationStart={() => setClipSearchRow(true)}
                            onAnimationComplete={() => setClipSearchRow(false)}
                            className="p-1 -m-1"
                            style={{
                                overflow: clipSearchRow ? 'hidden' : 'visible',
                                // content-box so the 4px of padding sits OUTSIDE
                                // the animated height. With the global
                                // border-box, `height: 0` still renders the
                                // padding and the row would never fully
                                // collapse; here height 0 really is 0, and the
                                // -m-1 cancels the padding's effect on layout.
                                boxSizing: 'content-box',
                            }}
                        >
                        <div className="flex gap-2 pb-4">
                            {/* Unified Search Input */}
                            <div className="relative flex-1" ref={filterDropdownRef}>
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={filterQuery}
                                onChange={handleFilterSearch}
                                onFocus={() => setShowFilterDropdown(true)}
                                onKeyDown={handleKeyDown}
                                placeholder="Search features, projects, or content..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action pr-10"
                            />
                            {isFilterSearching ? (
                                <div className="absolute right-0 top-0 h-full flex items-center pr-3 text-amber-800">
                                    <div className="animate-spin h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full"></div>
                                </div>
                            ) : filterQuery ? (
                                <motion.button
                                    onClick={() => {
                                        setFilterQuery('')
                                        setShowFilterDropdown(false)
                                        setSelectedItemIndex(-1)
                                    }}
                                    whileHover={{ scale: 1.15, rotate: 90 }}
                                    whileTap={{ scale: 0.9 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                    className="absolute right-0 top-0 h-full flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </motion.button>
                            ) : null}

                            {/* Filter Dropdown */}
                            <AnimatePresence>
                            {showFilterDropdown && filterQuery && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.97, y: -6 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.97, y: -6 }}
                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                    style={{ transformOrigin: 'top center' }}
                                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                    {/* Always show text search option at the top */}
                                    <div
                                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-b border-gray-100 ${
                                            selectedItemIndex === 0 ? 'bg-amber-50 border-amber-200' : ''
                                        }`}
                                        onClick={() => {
                                            const newSearchParams = new URLSearchParams(searchParams);
                                            
                                            // If date was found and remainder is empty, only set date range
                                            if (dateRangeResult?.date_found && !dateRangeResult.remainder?.trim()) {
                                                // Only date range, no text search
                                                setTextSearchQuery('');
                                                newSearchParams.set('start_date', dateRangeResult.start_date!);
                                                newSearchParams.set('end_date', dateRangeResult.end_date!);
                                            } else if (dateRangeResult?.date_found && dateRangeResult.remainder?.trim()) {
                                                // Both text search and date range
                                                setTextSearchQuery(dateRangeResult.remainder);
                                                newSearchParams.set('text_search', dateRangeResult.remainder);
                                                newSearchParams.set('start_date', dateRangeResult.start_date!);
                                                newSearchParams.set('end_date', dateRangeResult.end_date!);
                                                newSearchParams.set('date_remainder', dateRangeResult.remainder);
                                            } else {
                                                // No date found, just text search
                                                setTextSearchQuery(filterQuery);
                                                newSearchParams.set('text_search', filterQuery);
                                            }
                                            
                                            setSearchParams(newSearchParams);
                                            setFilterQuery('');
                                            setShowFilterDropdown(false);
                                            setSelectedItemIndex(-1);
                                        }}
                                    >
                                        <div className="flex items-center">
                                            {(!dateRangeResult?.date_found || dateRangeResult.remainder?.trim()) && (
                                                <>
                                                    <span className="font-medium">
                                                        Search for: "{dateRangeResult?.date_found && dateRangeResult.remainder ? dateRangeResult.remainder : filterQuery}"
                                                    </span>
                                                    <span className="ml-2 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                                        text search
                                                    </span>
                                                </>
                                            )}
                                            {dateRangeResult && dateRangeResult.date_found && (
                                                <>
                                                    {dateRangeResult.remainder?.trim() && <span className="mx-2 text-gray-400">•</span>}
                                                    <span className="font-medium">
                                                        {!dateRangeResult.remainder?.trim() && 'Search for: '}
                                                        {new Date(dateRangeResult.start_date!).toLocaleDateString()} - {new Date(dateRangeResult.end_date!).toLocaleDateString()}
                                                    </span>
                                                    <span className="ml-2 px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">
                                                        date range
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        <span className="text-gray-400">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </span>
                                    </div>
                                    
                                    {/* Filter results */}
                                    {filteredItems.map((filteredItem, index) => (
                                        <div
                                            key={`${filteredItem.type}-${filteredItem.item.id}`}
                                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center ${
                                                selectedItemIndex === index + 1 ? 'bg-amber-50 border-amber-200' : ''
                                            }`}
                                            onClick={() => {
                                                handleFilterSelect(filteredItem.type, filteredItem.item);
                                                setFilterQuery('');
                                                setShowFilterDropdown(false);
                                                setSelectedItemIndex(-1);
                                            }}
                                        >
                                            <div className="flex items-center">
                                                {filteredItem.type === 'project' && (filteredItem.item as Project).emoji && (
                                                    <span className="mr-2">{(filteredItem.item as Project).emoji}</span>
                                                )}
                                                {filteredItem.type === 'collection_item' && isLocationLikeItem(filteredItem.item as CollectionItem) && (
                                                    <span className="mr-1">📍</span>
                                                )}
                                                {filteredItem.type === 'collection_item' && isPersonLikeItem(filteredItem.item as CollectionItem) && (
                                                    <span className="mr-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full bg-sky-200 text-sky-800 text-[9px] font-semibold">
                                                        {personInitials(filteredItem.item as CollectionItem)}
                                                    </span>
                                                )}
                                                <span className="font-medium">
                                                    {filteredItem.type === 'collection_item'
                                                        ? (isPersonLikeItem(filteredItem.item as CollectionItem)
                                                            ? personDisplayName(filteredItem.item as CollectionItem)
                                                            : ((filteredItem.item as CollectionItem).data?.name
                                                                || (filteredItem.item as CollectionItem).metadata?.display_text
                                                                || `Item #${filteredItem.item.id}`))
                                                        : (filteredItem.item as Feature | Project).title
                                                    }
                                                </span>
                                                {filteredItem.type === 'collection_item'
                                                    && !isLocationLikeItem(filteredItem.item as CollectionItem)
                                                    && !isPersonLikeItem(filteredItem.item as CollectionItem)
                                                    && (filteredItem.item as CollectionItem).metadata?.collection_name && (
                                                    <span className="ml-1 text-xs text-gray-500">
                                                        from {(filteredItem.item as CollectionItem).metadata.collection_name}
                                                    </span>
                                                )}
                                                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                                    filteredItem.type === 'feature'
                                                        ? 'bg-amber-100 text-amber-900'
                                                        : filteredItem.type === 'project'
                                                        ? 'bg-plum-tint text-plum'
                                                        : isLocationLikeItem(filteredItem.item as CollectionItem)
                                                        ? 'bg-red-200 text-red-900'
                                                        : isPersonLikeItem(filteredItem.item as CollectionItem)
                                                        ? 'bg-sky-100 text-sky-800'
                                                        : 'bg-orange-100 text-orange-800'
                                                }`}>
                                                    {filteredItem.type === 'collection_item'
                                                        ? (isLocationLikeItem(filteredItem.item as CollectionItem)
                                                            ? 'location'
                                                            : isPersonLikeItem(filteredItem.item as CollectionItem)
                                                            ? 'person'
                                                            : 'collection item')
                                                        : filteredItem.type}
                                                </span>
                                            </div>
                                            <span className="text-gray-400">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                            </span>
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                            </AnimatePresence>
                            </div>

                            {/* Sort Dropdown */}
                            <div className="relative" ref={sortDropdownRef}>
                                <button
                                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                                    className="h-full p-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-action"
                                    title="Sort options"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4 4m0 0l4-4m-4 4V4" />
                                    </svg>
                                </button>
                                
                                {showSortDropdown && (
                                    <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-10">
                                        <div className="py-1">
                                            <button
                                                onClick={() => {
                                                    const newSearchParams = new URLSearchParams(searchParams)
                                                    const defaultOrder = textSearchQuery ? 'most_relevant' : 'most_recent'
                                                    if ('most_recent' === defaultOrder) {
                                                        newSearchParams.delete('order')
                                                    } else {
                                                        newSearchParams.set('order', 'most_recent')
                                                    }
                                                    setSearchParams(newSearchParams)
                                                    setShowSortDropdown(false)
                                                }}
                                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                                    sortOrder === 'most_recent' ? 'bg-gray-50 font-medium' : ''
                                                }`}
                                            >
                                                Most Recent
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const newSearchParams = new URLSearchParams(searchParams)
                                                    newSearchParams.set('order', 'oldest')
                                                    setSearchParams(newSearchParams)
                                                    setShowSortDropdown(false)
                                                }}
                                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                                    sortOrder === 'oldest' ? 'bg-gray-50 font-medium' : ''
                                                }`}
                                            >
                                                Oldest
                                            </button>
                                            {textSearchQuery && (
                                                <button
                                                    onClick={() => {
                                                        const newSearchParams = new URLSearchParams(searchParams)
                                                        const defaultOrder = 'most_relevant'
                                                        if ('most_relevant' === defaultOrder) {
                                                            newSearchParams.delete('order')
                                                        } else {
                                                            newSearchParams.set('order', 'most_relevant')
                                                        }
                                                        setSearchParams(newSearchParams)
                                                        setShowSortDropdown(false)
                                                    }}
                                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                                        sortOrder === 'most_relevant' ? 'bg-gray-50 font-medium' : ''
                                                    }`}
                                                >
                                                    Most Relevant
                                                </button>
                                            )}
                                            {/* Times, below a divider.
                                                This menu already governs how
                                                the list is *presented*, which
                                                is what the display mode is —
                                                and it is the discoverable half
                                                of the setting, where the date
                                                tap is the fast half. The hint
                                                is how one teaches the other.

                                                The same three words the
                                                account page uses, deliberately
                                                — one vocabulary met in three
                                                places beats two sets of words
                                                for one setting. */}
                                            <div className="mt-1 border-t border-gray-200 pt-1">
                                                <div className="px-4 py-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                                                    Times
                                                </div>
                                                {TIMESTAMP_DISPLAY_MODES.map(mode => (
                                                    <button
                                                        key={mode}
                                                        onClick={() => {
                                                            void chooseTimestampDisplay(mode)
                                                            setShowSortDropdown(false)
                                                        }}
                                                        aria-pressed={timestampMode === mode}
                                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                                            timestampMode === mode ? 'bg-gray-50 font-medium' : ''
                                                        }`}
                                                    >
                                                        {TIMESTAMP_DISPLAY_LABELS[mode]}
                                                    </button>
                                                ))}
                                                <p className="px-4 pt-1 pb-2 text-xs text-gray-500">
                                                    Or tap any date to change it.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        </motion.div>
                        )}
                        </AnimatePresence>

                        {/* Search Result Header and Active Filters Row */}
                        {(textSearchQuery || selectedFeatureId || selectedProjectId || selectedCollectionItemId || selectedDateRange) && (
                            <div className="flex flex-col gap-2">
                                {/* Active Filters */}
                                <div className="flex flex-wrap items-center gap-2">
                                    {selectedFeatureId && (
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-900">
                                            {features.find(f => f.id === selectedFeatureId)?.title}
                                            <button
                                                onClick={removeFeatureFilter}
                                                className="ml-2 text-amber-800 hover:text-amber-900"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </span>
                                    )}
                                    {selectedProjectId && (
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-plum-tint text-plum">
                                            {projects.find(p => p.id === selectedProjectId)?.emoji && (
                                                <span className="mr-1">{projects.find(p => p.id === selectedProjectId)?.emoji}</span>
                                            )}
                                            {projects.find(p => p.id === selectedProjectId)?.title}
                                            <button
                                                onClick={removeProjectFilter}
                                                className="ml-2 text-amber-800 hover:text-amber-900"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </span>
                                    )}
                                    {selectedCollectionItemId && (() => {
                                        const isLocation = isLocationLikeItem(collectionItemDisplayData)
                                        const isPerson = !isLocation && isPersonLikeItem(collectionItemDisplayData)
                                        const chipClass = isLocation
                                            ? 'inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-200 text-red-900'
                                            : isPerson
                                            ? 'inline-flex items-center pl-1 pr-3 py-0.5 rounded-full text-sm bg-sky-100 text-sky-900'
                                            : 'inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-800'
                                        const closeBtnClass = isLocation
                                            ? 'ml-2 text-red-700 hover:text-red-900 cursor-pointer'
                                            : isPerson
                                            ? 'ml-2 text-sky-700 hover:text-sky-900 cursor-pointer'
                                            : 'ml-2 text-orange-600 hover:text-orange-800 cursor-pointer'
                                        const label = isLocation
                                            ? (collectionItemDisplayData?.data?.name
                                                || collectionItemDisplayData?.metadata?.display_text
                                                || `Place #${selectedCollectionItemId}`)
                                            : isPerson
                                            ? personDisplayName(collectionItemDisplayData)
                                            : (collectionItemDisplayData?.metadata?.display_text
                                                || `Collection Item #${selectedCollectionItemId}`)
                                        const personPicture = isPerson && typeof collectionItemDisplayData?.data?.picture === 'string'
                                            ? collectionItemDisplayData!.data.picture.trim()
                                            : ''
                                        return (
                                            <span className={chipClass}>
                                                {isLocation && <span className="mr-1">📍</span>}
                                                {isPerson && (personPicture ? (
                                                    <img
                                                        src={personPicture}
                                                        alt=""
                                                        className="h-5 w-5 rounded-full object-cover bg-gray-200 mr-1.5"
                                                    />
                                                ) : (
                                                    <span className="mr-1.5 inline-flex items-center justify-center h-5 w-5 rounded-full bg-sky-200 text-sky-800 text-[10px] font-semibold">
                                                        {personInitials(collectionItemDisplayData)}
                                                    </span>
                                                ))}
                                                {label}
                                                {!isLocation && !isPerson && collectionItemDisplayData?.metadata?.collection_name && (
                                                    <span className="ml-1 text-xs opacity-75">
                                                        from {collectionItemDisplayData.metadata.collection_name}
                                                    </span>
                                                )}
                                                <button
                                                    onClick={removeCollectionItemFilter}
                                                    className={closeBtnClass}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </span>
                                        )
                                    })()}
                                    {textSearchQuery && (
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                                            Text about "{textSearchQuery}" or containing that term
                                            <button
                                                onClick={() => {
                                                    setTextSearchQuery('');
                                                    const newSearchParams = new URLSearchParams(searchParams);
                                                    newSearchParams.delete('text_search');
                                                    setSearchParams(newSearchParams);
                                                }}
                                                className="ml-2 text-green-600 hover:text-green-800 cursor-pointer"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </span>
                                    )}
                                    {selectedDateRange && (
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-800">
                                            Dates: {new Date(selectedDateRange.start_date).toLocaleDateString()} - {new Date(selectedDateRange.end_date).toLocaleDateString()}
                                            <button
                                                onClick={() => {
                                                    const newSearchParams = new URLSearchParams(searchParams);
                                                    newSearchParams.delete('start_date');
                                                    newSearchParams.delete('end_date');
                                                    newSearchParams.delete('date_remainder');
                                                    setSearchParams(newSearchParams);
                                                }}
                                                className="ml-2 text-amber-600 hover:text-amber-800 cursor-pointer"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </span>
                                    )}
                                    {(selectedFeatureId || selectedProjectId || selectedCollectionItemId || textSearchQuery || selectedDateRange) && (
                                        [selectedFeatureId, selectedProjectId, selectedCollectionItemId, textSearchQuery, selectedDateRange].filter(Boolean).length > 1
                                    ) && (
                                        <button
                                            onClick={clearFilters}
                                            className="text-sm text-gray-500 hover:text-gray-700 underline"
                                        >
                                            Clear all
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                )}

                {/* The way out of focus mode. With the search and filters
                    hidden and, on phones, the nav behind the hamburger, a
                    `?checkin_id=` page otherwise has no exit. It clears the
                    params instead of going back: a link from Parse Quality or
                    an email has no history entry to return to. `edit` goes
                    too, since it only means something alongside `checkin_id`;
                    the route itself (`/` or `/checkins`) is kept. */}
                {isSingleCheckinView && (
                    <div
                        className="mb-4 mx-4 sm:mx-0 flex items-center justify-between gap-3 rounded-md bg-secondary px-3 py-2 text-sm text-gray-700"
                        data-testid="focus-mode-bar"
                    >
                        <span className="min-w-0 truncate">Showing one check-in</span>
                        <motion.button
                            onClick={() => {
                                const next = new URLSearchParams(searchParams)
                                next.delete('checkin_id')
                                next.delete('edit')
                                setSearchParams(next)
                            }}
                            whileHover="hover"
                            whileTap={{ scale: 0.9 }}
                            variants={{ hover: { scale: 1.1 } }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                            className="flex shrink-0 items-center gap-1 whitespace-nowrap font-medium text-gray-800 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-action rounded cursor-pointer"
                        >
                            Show all
                            {/* The rotate lives on the icon, not the button:
                                spinning the label with it would be unreadable. */}
                            <motion.svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                                variants={{ hover: { rotate: 90 } }}
                                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </motion.svg>
                        </motion.button>
                    </div>
                )}

                {/* Checkin History */}
                <div className="bg-white p-3 sm:p-6 sm:rounded-lg sm:shadow-md relative">
                    {/* Search Loading Overlay */}
                    {searchLoading && (
                        <div className="absolute inset-0 bg-white/90 z-10 rounded-lg">
                            <div className="sticky flex justify-center" style={{ top: '50vh' }}>
                                    <Spinner />
                            </div>
                        </div>
                    )}
                    
                    {/* Selection mode controls */}
                    <AnimatePresence initial={false}>
                    {isSelectionMode && (
                        <motion.div
                            className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200"
                            initial={{ opacity: 0, scale: 0.95, y: -8 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -8 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                            style={{ transformOrigin: 'top center' }}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-600">
                                    {selectedCheckinIds.size} selected
                                </span>
                                <motion.button
                                    onClick={() => {
                                        const displayedCheckinIds = checkins
                                            .filter(c => c.id !== NEW_CHECKIN_ID_PLACEHOLDER)
                                            .map(c => c.id)
                                        const areAllSelected = displayedCheckinIds.every(id => selectedCheckinIds.has(id))

                                        if (areAllSelected) {
                                            // Deselect all
                                            setSelectedCheckinIds(new Set())
                                        } else {
                                            // Select all displayed checkins
                                            setSelectedCheckinIds(new Set(displayedCheckinIds))
                                        }
                                    }}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                    className="px-3 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                                >
                                    {(() => {
                                        const displayedCheckinIds = checkins
                                            .filter(c => c.id !== NEW_CHECKIN_ID_PLACEHOLDER)
                                            .map(c => c.id)
                                        const areAllSelected = displayedCheckinIds.every(id => selectedCheckinIds.has(id))
                                        return areAllSelected ? 'Select None' : 'Select All'
                                    })()}
                                </motion.button>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Mobile: grouped actions dropdown */}
                                <div className="relative sm:hidden" ref={bulkActionMenuRef}>
                                    <motion.button
                                        onClick={() => setShowBulkActionMenu(prev => !prev)}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                        className="px-3 py-1 bg-action text-plum rounded hover:bg-action-dark inline-flex items-center gap-1"
                                    >
                                        Actions
                                        <motion.svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            animate={{ rotate: showBulkActionMenu ? 180 : 0 }}
                                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </motion.svg>
                                    </motion.button>
                                    <AnimatePresence>
                                    {showBulkActionMenu && (
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.95, y: -8 }}
                                            animate={{ opacity: 1, scale: 1, y: 0 }}
                                            exit={{ opacity: 0, scale: 0.95, y: -8 }}
                                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                            style={{ transformOrigin: 'top right' }}
                                            className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-50"
                                        >
                                            <button
                                                onClick={() => {
                                                    setShowBulkActionMenu(false)
                                                    handleBulkDeleteCheckins(Array.from(selectedCheckinIds))
                                                }}
                                                disabled={selectedCheckinIds.size === 0}
                                                className="w-full text-left px-4 py-2 text-sm text-rose-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Delete
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowBulkActionMenu(false)
                                                    handleDiscussCheckins(Array.from(selectedCheckinIds))
                                                }}
                                                disabled={selectedCheckinIds.size === 0}
                                                className="w-full text-left px-4 py-2 text-sm text-amber-900 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Discuss
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowBulkActionMenu(false)
                                                    setShowReanalyzeConfirm(true)
                                                }}
                                                disabled={selectedCheckinIds.size === 0 || isBulkParsing}
                                                className="w-full text-left px-4 py-2 text-sm text-amber-900 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Analyze
                                            </button>
                                            <div className="border-t border-gray-100" />
                                            <button
                                                onClick={() => {
                                                    setShowBulkActionMenu(false)
                                                    setIsSelectionMode(false)
                                                    setSelectedCheckinIds(new Set())
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            >
                                                Cancel
                                            </button>
                                        </motion.div>
                                    )}
                                    </AnimatePresence>
                                </div>
                                {/* Desktop: inline buttons */}
                                <div className="hidden sm:flex items-center gap-2">
                                    <motion.button
                                        onClick={() => {
                                            if (selectedCheckinIds.size > 0) {
                                                handleBulkDeleteCheckins(Array.from(selectedCheckinIds))
                                            }
                                        }}
                                        disabled={selectedCheckinIds.size === 0}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                        className="px-3 py-1 bg-danger text-white rounded hover:bg-danger-dark disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Delete
                                    </motion.button>
                                    <motion.button
                                        onClick={() => {
                                            if (selectedCheckinIds.size > 0) {
                                                handleDiscussCheckins(Array.from(selectedCheckinIds))
                                            }
                                        }}
                                        disabled={selectedCheckinIds.size === 0}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                        className="px-3 py-1 bg-action text-plum rounded hover:bg-action-dark disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Discuss
                                    </motion.button>
                                    <motion.button
                                        onClick={() => {
                                            if (selectedCheckinIds.size > 0) {
                                                setShowReanalyzeConfirm(true)
                                            }
                                        }}
                                        disabled={selectedCheckinIds.size === 0 || isBulkParsing}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                        className="px-3 py-1 bg-action text-plum rounded hover:bg-action-dark disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Analyze
                                    </motion.button>
                                    <motion.button
                                        onClick={() => {
                                            setIsSelectionMode(false)
                                            setSelectedCheckinIds(new Set())
                                        }}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                        className="px-3 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                                    >
                                        Cancel
                                    </motion.button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                    </AnimatePresence>
                    
                    {checkins.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <p>No checkins yet. Type a note above to create your first checkin.</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                        {/* Neither of these renders an element of its own, so
                            the rows below stay direct children of the spacing
                            container. Under `most_relevant` nothing inside
                            arms a sortable, and the context is inert. */}
                        <DndContext
                            sensors={reorderSensors}
                            collisionDetection={closestCenter}
                            onDragStart={() => setReorderDragging(true)}
                            onDragCancel={() => setReorderDragging(false)}
                            onDragEnd={handleReorderDragEnd}
                        >
                        <SortableContext
                            items={reorderEnabled ? displaySlots.map(slotId) : checkins.map(c => c.id)}
                            strategy={reorderEnabled ? reorderStrategy(displaySlots) : verticalListSortingStrategy}
                        >

                            {(() => {
                                // Only group by year for "most_recent" and "oldest" sort orders
                                if (sortOrder === 'most_relevant') {
                                    // For "most_relevant", render checkins without year grouping
                                    return checkins.map((unfilteredCheckin, index) => {
                                        const checkin = filteredCheckins.get(unfilteredCheckin.id)!
                                        return (
                                            <CheckinRow key={keyFor(checkin)} landed={checkin.id === landedCheckinId} nudge={nudgeFor(checkin)}>
                                            <CheckinItem
                                                checkin={checkin}
                                                index={index}
                                                showSeparator={index > 0}
                                                editingCheckinId={editingCheckinId}
                                                showNewCheckinComposer={showNewCheckinComposer}
                                                features={features}
                                                recentFeatures={recentFeatures}
                                                sortOrder={sortOrder}
                                                savingState={savingState}
                                                parseCountdown={parseCountdown?.checkinId === checkin.id ? parseCountdown : null}
                                                onParseNow={flushAIParse}
                                                onComposerClose={closeComposer}
                                                activity={activityFor(checkin.id)}
                                                onToggleParseExpanded={(expanded) => handleToggleParseExpanded(checkin.id, expanded)}
                                                onToggleParseTechnical={(technical) => handleToggleParseTechnical(checkin.id, technical)}
                                                onReviewParse={handleReviewParse}
                                                collectionItems={collectionItems}
                                                availableActions={['edit', 'duplicate', 'save-as-template', 'delete', 'reanalyze', 'discuss']}
                                                onDiscuss={(id) => handleDiscussCheckins([id])}
                                                onSaveAsTemplate={handleSaveAsTemplate}
                                                formatDate={formatDate}
                                                getComposerCheckinFromCheckin={getComposerCheckinFromCheckin}
                                                handleFieldEdit={handleFieldEdit}
                                                handleDeleteCheckin={handleDeleteCheckin}
                                                handleEditCheckin={handleEditCheckin}
                                                handleDuplicateCheckin={handleDuplicateCheckin}
                                                handleFilterSelect={handleFilterSelect}
                                                handleReanalyzeCheckin={handleReanalyzeCheckin}
                                                onRefresh={fetchData}
                                                onCollectionItemReady={(item) =>
                                                    mergeCollectionItems({ [item.id]: item })
                                                }
                                                setEditingCheckinId={setEditingCheckinId}
                                                setCheckins={setCheckins}
                                                setShowNewCheckinComposer={setShowNewCheckinComposer}
                                                firstElementIsEmptyCheckin={firstElementIsEmptyCheckin}
                                                isSelectionMode={isSelectionMode}
                                                isSelected={selectedCheckinIds.has(checkin.id)}
                                                onToggleSelection={(id) => {
                                                    setSelectedCheckinIds(prev => {
                                                        const newSet = new Set(prev)
                                                        if (newSet.has(id)) {
                                                            newSet.delete(id)
                                                        } else {
                                                            newSet.add(id)
                                                        }
                                                        return newSet
                                                    })
                                                }}
                                            />
                                            </CheckinRow>
                                        )
                                    });
                                } else {
                                    // Group checkins by year for "most_recent" and "oldest"
                                    const yearGroups = groupByYear(checkins, sortOrder === 'oldest')
                                    const checkinsGroupedByYear = Object.fromEntries(
                                        yearGroups.map(g => [g.year, g.rows]),
                                    ) as Record<number, Checkin[]>
                                    const sortedYears = yearGroups.map(g => g.year)
                                    // The divider, if any, that stands just above each row.
                                    const dividerAbove = new Map<number, BoundarySlot>()
                                    displaySlots.forEach((slot, i) => {
                                        const next = displaySlots[i + 1]
                                        if (isDayBoundary(slot) && next && !isDayBoundary(next)) {
                                            dividerAbove.set(next.id, slot as BoundarySlot)
                                        }
                                    })

                                    // Render checkins grouped by year
                                    return sortedYears.map((year, yearIndex) => (
                                        <React.Fragment key={year}>
                                            {/* Year separator - show before group for oldest, after for most_recent */}
                                            {sortOrder === 'oldest' && year !== new Date().getFullYear() && (
                                                <div className="py-2 mb-4 text-center">
                                                    <div className="flex items-center justify-center">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                        </svg>
                                                        <span className="font-medium text-lg text-gray-500 ml-1">{year}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {checkinsGroupedByYear[year].map((unfilteredCheckin, index) => {
                                                const checkin = filteredCheckins.get(unfilteredCheckin.id)!
                                                const divider = dividerAbove.get(unfilteredCheckin.id)
                                                return (
                                                    <React.Fragment key={keyFor(checkin)}>
                                                    {divider && (
                                                        <DayDivider
                                                            id={divider.key}
                                                            label={formatDate(divider.below)}
                                                            visible={reorderDragging}
                                                        />
                                                    )}
                                                    <SortableCheckin
                                                        id={checkin.id}
                                                        disabled={
                                                            !reorderEnabled
                                                            || checkin.id === NEW_CHECKIN_ID_PLACEHOLDER
                                                            || isSelectionMode
                                                            || editingCheckinId !== null
                                                        }
                                                    >
                                                    {(handle) => (
                                                    <CheckinRow landed={checkin.id === landedCheckinId} nudge={nudgeFor(checkin)}>
                                                    <CheckinItem
                                                        checkin={checkin}
                                                        dragHandle={handle}
                                                        timeFlash={timeFlashes[checkin.id] ?? null}
                                                        index={index}
                                                        showSeparator={index > 0}
                                                        editingCheckinId={editingCheckinId}
                                                        showNewCheckinComposer={showNewCheckinComposer}
                                                        features={features}
                                                        recentFeatures={recentFeatures}
                                                        sortOrder={sortOrder}
                                                        savingState={savingState}
                                                        parseCountdown={parseCountdown?.checkinId === checkin.id ? parseCountdown : null}
                                                        onParseNow={flushAIParse}
                                                        onComposerClose={closeComposer}
                                                        activity={activityFor(checkin.id)}
                                                        onToggleParseExpanded={(expanded) => handleToggleParseExpanded(checkin.id, expanded)}
                                                onToggleParseTechnical={(technical) => handleToggleParseTechnical(checkin.id, technical)}
                                                        onReviewParse={handleReviewParse}
                                                        collectionItems={collectionItems}
                                                        availableActions={['edit', 'duplicate', 'save-as-template', 'delete', 'reanalyze', 'discuss']}
                                                        onDiscuss={(id) => handleDiscussCheckins([id])}
                                                        onSaveAsTemplate={handleSaveAsTemplate}
                                                        formatDate={formatDate}
                                                        getComposerCheckinFromCheckin={getComposerCheckinFromCheckin}
                                                        handleFieldEdit={handleFieldEdit}
                                                        handleDeleteCheckin={handleDeleteCheckin}
                                                        handleEditCheckin={handleEditCheckin}
                                                        handleDuplicateCheckin={handleDuplicateCheckin}
                                                        handleFilterSelect={handleFilterSelect}
                                                        handleReanalyzeCheckin={handleReanalyzeCheckin}
                                                        onRefresh={fetchData}
                                                        setEditingCheckinId={setEditingCheckinId}
                                                        setCheckins={setCheckins}
                                                        setShowNewCheckinComposer={setShowNewCheckinComposer}
                                                        firstElementIsEmptyCheckin={firstElementIsEmptyCheckin}
                                                        isSelectionMode={isSelectionMode}
                                                        isSelected={selectedCheckinIds.has(checkin.id)}
                                                        onToggleSelection={(id) => {
                                                            setSelectedCheckinIds(prev => {
                                                                const newSet = new Set(prev)
                                                                if (newSet.has(id)) {
                                                                    newSet.delete(id)
                                                                } else {
                                                                    newSet.add(id)
                                                                }
                                                                return newSet
                                                            })
                                                        }}
                                                    />
                                                    </CheckinRow>
                                                    )}
                                                    </SortableCheckin>
                                                    </React.Fragment>
                                                )
                                            })}
                                            {/* Year separator - show after group for most_recent */}
                                            {sortOrder === 'most_recent' && yearIndex < sortedYears.length - 1 && (
                                                // Only show year separator if it's not the current year
                                                year !== new Date().getFullYear() && (
                                                    <div className="py-2 mb-4 text-center">
                                                        <div className="flex items-center justify-center">
                                                            <span className="font-medium text-lg text-gray-500 mr-1">{year}</span>
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                )
                                            )}

                                        </React.Fragment>
                                    ));
                                }
                            })()}
                        </SortableContext>
                        </DndContext>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Explains why we're about to ask for location. Shown once ever,
                immediately before the browser's own prompt. */}
            <LocationPrimerModal
                isOpen={primerOpen}
                onAccept={handlePrimerAccept}
                onDecline={handlePrimerDecline}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteConfirmation.isOpen}
                onClose={() => setDeleteConfirmation({ isOpen: false, checkinId: null, checkinIds: undefined, isDeleting: false })}
                onConfirm={() => {
                    if (deleteConfirmation.checkinId !== null) {
                        handleDeleteCheckin(deleteConfirmation.checkinId, true)
                    } else if (deleteConfirmation.checkinIds) {
                        handleBulkDeleteCheckins(deleteConfirmation.checkinIds, true)
                    }
                }}
                title={deleteConfirmation.checkinIds ? `Delete ${deleteConfirmation.checkinIds.length} Checkin${deleteConfirmation.checkinIds.length === 1 ? '' : 's'}` : "Delete Checkin"}
                message={deleteConfirmation.checkinIds ? 
                    `Are you sure you want to delete ${deleteConfirmation.checkinIds.length} checkin${deleteConfirmation.checkinIds.length === 1 ? '' : 's'}? This action cannot be undone.` :
                    "Are you sure you want to delete this checkin? This action cannot be undone."}
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonClass="bg-danger hover:bg-danger-dark text-white"
                isLoading={deleteConfirmation.isDeleting}
            />

            {/* Analyze Confirmation Modal */}
            <ConfirmationModal
                isOpen={showReanalyzeConfirm}
                onClose={() => { if (!isBulkParsing) setShowReanalyzeConfirm(false) }}
                onConfirm={executeBulkReanalyze}
                title={`Analyze ${selectedCheckinIds.size} Checkin${selectedCheckinIds.size === 1 ? '' : 's'}`}
                message={`Analyze ${selectedCheckinIds.size} checkin${selectedCheckinIds.size === 1 ? '' : 's'} again? This can be expensive.`}
                confirmText="Analyze"
                cancelText="Cancel"
                confirmButtonClass="bg-action hover:bg-action-dark text-plum"
                isLoading={isBulkParsing}
            />

            {/* Asking for a day's times, when a drop had nothing to order
                against. Cancel writes nothing and leaves the drag undone. */}
            <AnimatePresence>
                {timeAssignment && (
                    <CheckinTimeAssignmentDialog
                        key="time-assignment"
                        day={timeAssignment.day}
                        rows={timeAssignment.rows}
                        placingId={timeAssignment.placingId}
                        placingMovesDay={timeAssignment.movesDay}
                        onSuggestTimes={ids => suggestCheckinTimes(ids, timeAssignment.day)}
                        onCancel={() => setTimeAssignment(null)}
                        onConfirm={handleTimeAssignmentConfirm}
                    />
                )}
            </AnimatePresence>

        </div>
    )
}



export default CheckinsPage
